(() => {
  if (window.RoniniCartEditorLoaded) return;
  window.RoniniCartEditorLoaded = true;

  const sizeMeta = {
    '160': { size: '160ml', flow: 'sip', bonus: 'drop', combination: 'Sip+Drop', model: 'RNB-160G2' },
    '240': { size: '240ml', flow: 'stream', bonus: 'sip', combination: 'Stream+Sip', model: 'RNB-240G2' }
  };

  const cartVersion = '4';
  const initialCartElement = document.getElementById('ronini-cart-initial-state');
  let initialCart = null;
  try {
    initialCart = initialCartElement ? JSON.parse(initialCartElement.textContent) : null;
  } catch (error) {
    initialCart = null;
  }

  let currentCart = initialCart?.items ? initialCart : null;
  let currentCartRememberedAt = currentCart ? Date.now() : 0;
  let cartRequest = null;
  let userMutationRunning = false;
  let cartIdleResolvers = [];
  let legacyCartMigration = null;
  const pendingPackageQuantities = new Map();
  let packageQuantityTimer = null;
  let packageQuantityRequestRunning = false;
  let giftReconciliationPromise = null;
  // The single gift pass currently writing to the cart (if any). Package edits
  // wait for it so two writers never race on line keys.
  let giftPassPromise = null;
  const waitForGiftPass = () => (giftPassPromise ? giftPassPromise.catch(() => null) : Promise.resolve());
  const pendingReplacementKey = 'ronini:pending-cart-replacement';

  const fetchWithTimeout = (url, options = {}, timeoutMs = 8000) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal })
      .finally(() => window.clearTimeout(timeout));
  };

  const finishUserMutation = () => {
    userMutationRunning = false;
    const resolvers = cartIdleResolvers;
    cartIdleResolvers = [];
    resolvers.forEach((resolve) => resolve());
  };

  const waitForCartIdle = () => (
    userMutationRunning
      ? new Promise((resolve) => { cartIdleResolvers.push(resolve); })
      : Promise.resolve()
  );

  const rememberCart = (cart) => {
    if (cart?.items) {
      currentCart = cart;
      currentCartRememberedAt = Date.now();
    }
    return cart;
  };

  const getCart = async (force = false) => {
    const cartCacheIsFresh = Date.now() - currentCartRememberedAt < 10000;
    if (!force && cartCacheIsFresh && currentCart?.items) return currentCart;
    if (cartRequest) return cartRequest;

    cartRequest = fetchWithTimeout(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }, 6000)
      .then((response) => {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      })
      .then(rememberCart)
      .finally(() => { cartRequest = null; });

    return cartRequest;
  };

  const mainCartSection = () => document.querySelector('[data-ronini-main-cart][data-section-id]');

  const setPackageMutationState = (lineItem, busy, removing = false) => {
    const packageElement = lineItem?.closest('.rnc-item, .ronini-cart-kit') || lineItem;
    if (!packageElement) return;

    packageElement.toggleAttribute('aria-busy', busy);
    if (busy && removing) {
      packageElement.classList.add('is-removing');
    } else if (!busy) {
      packageElement.classList.remove('is-removing');
    }
  };

  const renderMainCart = (cart) => {
    const current = mainCartSection();
    const sectionId = current?.dataset.sectionId;
    const sectionHtml = sectionId && cart?.sections?.[sectionId];
    if (!current || !sectionHtml) return false;

    const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const replacement = documentFragment.querySelector(`#shopify-section-${CSS.escape(sectionId)} [data-ronini-main-cart]`)
      || documentFragment.querySelector('[data-ronini-main-cart]');
    if (!replacement) return false;

    current.replaceChildren(...document.createRange().createContextualFragment(replacement.innerHTML).children);
    return true;
  };

  document.documentElement.addEventListener('cart:prepare-bundled-sections', (event) => {
    const sectionId = mainCartSection()?.dataset.sectionId;
    if (sectionId) event.detail?.sections?.add(sectionId);
  });

  // SF01 for the standalone cart page: a change answered without this section (sections: null) is re-fetched,
  // with the same refreshing / stale flags the drawer uses so Checkout waits for the authoritative total.
  const refreshMainCart = async (attempt = 1) => {
    const current = mainCartSection();
    const sectionId = current?.dataset.sectionId;
    if (!current || !sectionId) return;
    current.setAttribute('data-cart-refreshing', '');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=${encodeURIComponent(sectionId)}&_=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`main cart ${response.status}`);
      if (!renderMainCart({ sections: { [sectionId]: await response.text() } })) throw new Error('main cart markup missing');
      current.removeAttribute('data-cart-refreshing');
      current.removeAttribute('data-cart-stale');
    } catch (error) {
      if (attempt < 3) {
        window.setTimeout(() => refreshMainCart(attempt + 1), attempt * 800);
      } else {
        current.removeAttribute('data-cart-refreshing');
        current.setAttribute('data-cart-stale', '');
      }
    }
  };

  document.documentElement.addEventListener('cart:change', (event) => {
    rememberCart(event.detail?.cart);
    if (!renderMainCart(event.detail?.cart) && mainCartSection()) refreshMainCart(1);
    if (stockNotices.size) [0, 400, 1200].forEach((delay) => window.setTimeout(showStockNotices, delay));
  });

  document.addEventListener('cart:refresh', () => {
    currentCart = null;
    currentCartRememberedAt = 0;
  });
  // TC041 (10-09): the standalone cart page restored from bfcache (back/forward, pageshow.persisted) shows the
  // cart as it was when the page was left. Like the drawer (theme.js onPageShow_fn), re-read /cart.js and the
  // section and re-render; the same when the tab becomes visible again and the cart changed meanwhile.
  const cartDigest = (cart) => (cart?.items || []).map((item) => {
    const props = item.properties || {};
    const visible = Object.keys(props).filter((key) => !key.startsWith('_') || key === '_due_date' || key === '_bonus_nipple_flow' || key === '_entry_age')
      .sort().map((key) => `${key}=${props[key]}`).join('&');
    return `${item.key}:${item.quantity}:${item.selling_plan_allocation?.selling_plan?.id || ''}:${visible}`;
  }).join('|');
  const resyncMainCart = async ({ onlyIfChanged = false } = {}) => {
    const before = currentCart;
    currentCart = null;
    currentCartRememberedAt = 0;
    if (!mainCartSection()) return;
    let fresh = null;
    try {
      fresh = await getCart(true);
    } catch (error) {
      fresh = null;
    }
    // V01 (review 10-09): same token / count / total can still hide a content change made in another tab
    // (gift flow, baby stage, due date…), so the line keys, quantities and visible properties are compared too.
    if (onlyIfChanged && before?.items && fresh?.items
      && before.token === fresh.token && before.item_count === fresh.item_count
      && before.total_price === fresh.total_price
      && cartDigest(before) === cartDigest(fresh)) return;
    if (cartWorkIsPending()) return;
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    rememberCart(fresh);
    refreshMainCart(1);
  };
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resyncMainCart();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resyncMainCart({ onlyIfChanged: true });
  });

  const cartWorkIsPending = () => (
    userMutationRunning
    || packageQuantityRequestRunning
    || pendingPackageQuantities.size > 0
    || !!legacyCartMigration
    || !!giftReconciliationPromise
  );

  // A02: a welcome-kit repair that failed (network, rejected add) is remembered, so
  // checkout never leaves with a subscription set that is missing its free kit
  // without the customer being told and given a retry.
  let giftRepairFailed = false;
  let checkoutKitWarnings = 0;
  const checkoutErrorClass = 'ronini-checkout-error';
  const showCheckoutError = (control, message) => {
    document.querySelectorAll(`.${checkoutErrorClass}`).forEach((node) => node.remove());
    if (!control || !message) return;
    const note = document.createElement('p');
    note.className = `${checkoutErrorClass} text-xs`;
    note.setAttribute('role', 'alert');
    note.style.cssText = 'margin:0 0 8px;color:#230052;background:#FFFBF1;border:1px solid #FC4C02;border-radius:8px;padding:8px 12px;text-align:start;';
    note.textContent = message;
    control.insertAdjacentElement('beforebegin', note);
  };

  const waitForAllCartWork = async () => {
    // Cart mutations can synchronously schedule a gift reconciliation. Re-read
    // the active promises after every settle so checkout cannot slip between
    // the package write and its cart-owned welcome-gift repair.
    while (cartWorkIsPending()) {
      if (pendingPackageQuantities.size && !packageQuantityRequestRunning) {
        clearTimeout(packageQuantityTimer);
        packageQuantityTimer = null;
        flushPackageQuantities();
      }
      const pending = [];
      if (userMutationRunning) pending.push(waitForCartIdle());
      if (legacyCartMigration) pending.push(legacyCartMigration.catch(() => null));
      if (giftReconciliationPromise) pending.push(giftReconciliationPromise.catch(() => null));
      if (!pending.length) {
        await new Promise((resolve) => window.setTimeout(resolve, 20));
      } else {
        await Promise.all(pending);
      }
    }
    return getCart(true);
  };

  document.addEventListener('click', (event) => {
    const checkoutControl = event.target.closest(
      'a.ronini-checkout-link, a.rnc-checkout, a.ronini-cart-actions__checkout, button[name="checkout"], input[name="checkout"]'
    );
    if (!checkoutControl) return;
    if (checkoutControl.dataset.roniniCartVerified === 'true') {
      delete checkoutControl.dataset.roniniCartVerified;
      return;
    }
    // Review 2026-10-05 SF01: never leave for checkout while the drawer / cart page shows a total the server has
    // already changed (section refetch pending or failed). Ask for a fresh render and explain.
    const staleRoot = checkoutControl.closest('[data-cart-stale], [data-cart-refreshing]');
    if (staleRoot) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const failed = staleRoot.hasAttribute('data-cart-stale');
      showCheckoutError(checkoutControl, failed
        ? 'We couldn’t update your cart total. Check your connection; we’re trying again. Press Checkout once the new total shows.'
        : 'Your cart total is updating. One moment, then press Checkout again.');
      if (failed) {
        if (staleRoot.matches('[data-ronini-main-cart]')) refreshMainCart(1);
        else document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
      return;
    }
    if (!cartWorkIsPending() && !giftRepairFailed) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutControl.getAttribute('aria-busy') === 'true') return;
    checkoutControl.setAttribute('aria-busy', 'true');
    checkoutControl.setAttribute('aria-disabled', 'true');
    showCheckoutError(checkoutControl, '');
    const release = () => {
      checkoutControl.removeAttribute('aria-busy');
      checkoutControl.removeAttribute('aria-disabled');
    };
    waitForAllCartWork()
      .then(async (cart) => {
        // The wait ends when the work stops, not when it succeeded: check that every
        // subscription set holds its welcome kit, retry the repair once, and only
        // then leave for checkout (A02).
        let verified = cart;
        if (giftRepairFailed || welcomeKitIssues(verified).length) {
          await reconcileWelcomeGifts().catch(() => null);
          verified = await getCart(true);
        }
        // Two failed repairs are reported with a retry; after that the customer may
        // knowingly continue (e.g. the gift is out of stock) rather than be locked out.
        if (welcomeKitIssues(verified).length && checkoutKitWarnings < 2) {
          giftRepairFailed = true;
          checkoutKitWarnings += 1;
          release();
          showCheckoutError(checkoutControl, checkoutKitWarnings < 2
            ? 'We couldn’t add your free Box 1 welcome kit to the cart. Check your connection, then press Checkout again.'
            : 'Your free Box 1 welcome kit still couldn’t be added. Press Checkout again to continue without it, or email hi@ronini.com and we’ll put it right.');
          return;
        }
        giftRepairFailed = false;
        checkoutKitWarnings = 0;
        if (checkoutControl instanceof HTMLAnchorElement) {
          window.location.assign(checkoutControl.href);
          return;
        }
        release();
        checkoutControl.dataset.roniniCartVerified = 'true';
        checkoutControl.click();
      })
      .catch(() => {
        release();
        showCheckoutError(checkoutControl, 'We couldn’t confirm your cart. Check your connection, then press Checkout again.');
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
  }, true);

  // 10-21 A/B test w2 (snippets/rn-ab.liquid): Shopify's express buttons ([data-rn-express], version b) start payment the
  // moment they're pressed, so they can't wait for the check above. They show only while that check would pass at once:
  // no cart work pending, no failed welcome-gift repair, every subscription set holding its welcome kit, and the drawer
  // total not stale. Any cart change hides them until the cart is checked again.
  let expressRun = 0;
  let expressTimer = null;
  const expressNodes = () => Array.from(document.querySelectorAll('[data-rn-express]'));
  const syncExpress = () => {
    const nodes = expressNodes();
    if (!nodes.length) return;
    const hideAll = () => expressNodes().forEach((node) => { node.hidden = true; });
    if (document.documentElement.getAttribute('data-ab-w2') !== 'b') { hideAll(); return; }
    const run = ++expressRun;
    if (cartWorkIsPending() || giftRepairFailed) hideAll();
    waitForAllCartWork().then((cart) => {
      if (run !== expressRun) return;
      const ready = !giftRepairFailed && !cartWorkIsPending() && Number(cart?.item_count || 0) > 0 && !welcomeKitIssues(cart).length;
      expressNodes().forEach((node) => { node.hidden = !ready || !!node.closest('[data-cart-stale], [data-cart-refreshing]'); });
    }).catch(() => { if (run === expressRun) hideAll(); });
  };
  const scheduleExpress = () => { clearTimeout(expressTimer); expressTimer = window.setTimeout(syncExpress, 60); };
  document.documentElement.addEventListener('cart:change', () => { expressNodes().forEach((node) => { node.hidden = true; }); scheduleExpress(); });
  document.addEventListener('cart:refresh', scheduleExpress);
  new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1
      && (node.matches?.('[data-rn-express]') || node.querySelector?.('[data-rn-express]'))))) scheduleExpress();
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleExpress); else scheduleExpress();

  // Same cohort rule as the PDP: step back whole calendar months from the first
  // of the current month (never setMonth() on the 29th-31st) in local time.
  const birthMonth = (age) => {
    if (age === 'expecting') return '';
    const months = Number(String(age).replace('mo', '')) || 0;
    const today = new Date();
    const date = new Date(today.getFullYear(), today.getMonth() - months, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  // A refill follows the parent's chosen flow. Age can power a suggestion in
  // My Subscription, but it must never silently make the next shipment faster (ENG02),
  // including the refill that ships with the 240 ml upgrade.
  // Owner 09-30: Expecting Box 1 ships now; refills are anchored to the due
  // date (first refill = due date + 2 months, 240 ml upgrade = due + 4 months).
  const addMonthsClamped = (date, months) => {
    const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(date.getDate(), lastDay));
    return result;
  };
  const fullMonthsFromToday = (target) => {
    const today = calendarToday();
    let months = (target.getFullYear() - today.getFullYear()) * 12 + target.getMonth() - today.getMonth();
    if (addMonthsClamped(today, months) > target) months -= 1;
    return Math.max(1, months);
  };
  const longDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const fulfillmentSchedule = (age, size, dueDate) => {
    const month = age === 'expecting' ? 0 : Number(String(age).replace('mo', '')) || 0;
    const flow = sizeMeta[size]?.flow || 'sip';
    if (age === 'expecting') {
      const due = parseIsoDate(dueDate);
      const first = due ? addMonthsClamped(due, 2) : null;
      return {
        delay: first ? fullMonthsFromToday(first) : 2,
        babyMonth: 2,
        flow,
        kind: 'expecting',
        contents: `${flow}_nipple_refill`,
        nextShipDate: first ? toIsoDate(first) : '',
        nextShipLabel: first ? longDate(first) : ''
      };
    }
    if (size === '160' && month === 2) {
      return { delay: 2, babyMonth: 4, flow, kind: 'together', contents: `240ml_upgrade_box+${flow}_nipple_refill` };
    }
    if (size === '160' && month === 3) {
      return { delay: 1, babyMonth: 4, flow, kind: 'split', contents: `240ml_upgrade_box_then_${flow}_nipple_refill` };
    }
    return { delay: 2, babyMonth: month + 2, flow, kind: 'refill', contents: `${flow}_nipple_refill` };
  };

  const withRefillFlow = (label, flowLabel) => String(label || '')
    .replace('__FLOW__', flowLabel)
    .replace(/\b(?:Drop|Sip|Stream|Rapid)(?= nipple refill)/g, flowLabel);

  // QA 09-30: a subscription Duo set gets 2 nipple refill 2-packs per refill (worker packsPerKit = 2).
  const forSet = (props, label) => (props?._duo_flag === 'true'
    ? String(label || '').replace(/\b([A-Z][a-z]+) nipple refill(?: 2-pack)?/g, '2 × $1 nipple refill 2-packs')
    : label);

  const nextShipmentLabel = (editor, schedule) => {
    const copy = editor?.dataset || {};
    const flow = schedule.flow || schedule.contents.replace(/^.*?(\w+)_nipple_refill$/, '$1');
    const flowLabel = `${flow.charAt(0).toUpperCase()}${flow.slice(1)}`;
    if (schedule.kind === 'together') {
      return withRefillFlow(copy.nextShipmentUpgradeTogether || 'In 2 months · 8 oz / 240 ml upgrade box + __FLOW__ nipple refill', flowLabel);
    }
    if (schedule.kind === 'expecting') {
      return withRefillFlow(
        String(copy.nextShipmentExpecting || '__DATE__ · __FLOW__ nipple refill 2-pack')
          .replace('__DATE__', schedule.nextShipLabel || '2 months after your due date'),
        flowLabel);
    }
    if (schedule.kind === 'split') {
      return withRefillFlow(copy.nextShipmentUpgradeSplit || 'In 1 month · 8 oz / 240 ml upgrade box; in 2 months · __FLOW__ nipple refill', flowLabel);
    }
    return withRefillFlow(copy.nextShipmentNipple || 'In 2 months · __FLOW__ nipple refill 2-pack', flowLabel);
  };

  // 10-21 (audit F01): a 5 oz subscription set started before four months states its 8 oz upgrade charge in words, since
  // checkout only shows the plan's regular refill. Same sentence as the product page (pdp-buybox-configurator.js
  // upgradeChargeProperty); prices come from the product page in _upgrade_prices (wrapped,duo,refill,shipping, cents).
  // Lines without them (added before 10-21) get no note rather than a guessed price.
  const UPGRADE_NOTE_KEY = '8 oz upgrade';
  const centsText = (cents) => `$${cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2)}`;
  const upgradeNote = (props, age, size, dueDate) => {
    const [wrapped, duoSet, refill, shipping] = String(props?._upgrade_prices || '').split(',').map(Number);
    if (size !== '160' || !(wrapped > 0) || !(duoSet > 0) || !(refill >= 0) || !(shipping >= 0)) return '';
    const duo = props?._duo_flag === 'true';
    let when;
    let sameDay;
    if (age === 'expecting') {
      const due = parseIsoDate(dueDate);
      if (!due) return '';
      when = addMonthsClamped(due, 4);
      sameDay = true;
    } else {
      const month = Number(String(age).replace('mo', ''));
      if (!(month >= 0 && month < 4)) return '';
      when = addMonthsClamped(calendarToday(), 4 - month);
      sameDay = (4 - month) % 2 === 0;
    }
    return `Around ${longDate(when)} · ${centsText(duo ? duoSet : wrapped)}${duo ? ' per Duo set of 4 bottles' : ' per 2 bottles'}`
      + (sameDay ? `, plus that box’s ${duo ? `two ${centsText(refill)} refills` : `${centsText(refill)} refill`} and ` : ', plus ')
      + `${centsText(shipping)} shipping · keep 5 oz in My Subscription to skip it`;
  };
  const setUpgradeNote = (props, age, size, dueDate) => {
    const note = upgradeNote(props, age, size, dueDate);
    if (note) props[UPGRADE_NOTE_KEY] = note;
    else delete props[UPGRADE_NOTE_KEY];
  };

  const publicTechnicalProperties = [
    'baby_stage', 'birth_month', 'initial_size', 'bottle_capacity_ml',
    'initial_flow', 'bonus_nipple_flow', 'flow_combination', 'ronini_model',
    'anti_colic_configuration', 'duo_flag', 'subscription_program',
    'delivery_interval_months', 'next_shipment_in_months',
    'next_shipment_baby_age_months', 'next_shipment_contents',
    'recharge_first_charge_delay_months', 'ronini_fulfillment_program',
    'ronini_qa_mode', 'selected_variant', 'selling_plan_name', 'due_date',
    'Baby stage', 'Bottle setup', 'Next shipment', '8 oz upgrade'
  ];

  const ageLabel = (age, dueDate) => {
    if (age === 'expecting') {
      if (!dueDate) return 'Expecting';
      const parsed = new Date(`${dueDate}T00:00:00`);
      return Number.isNaN(parsed.valueOf())
        ? 'Expecting'
        : `Expecting · due ${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
    }
    const month = Number(String(age).replace('mo', '')) || 0;
    if (month === 0) return 'Newborn';
    return month === 1 ? '1 month' : `${month} months`;
  };

  // Oz-first size label (owner 09-30); window.roniniSizeLabel lives in layout/theme.liquid.
  const sizeLabelFor = (size) => (window.roniniSizeLabel ? window.roniniSizeLabel(size) : `${size} ml`);

  const setEditorSavingState = (editor, saving, nextState = null) => {
    const details = editor.querySelector('details');
    const current = editor.querySelector('.ronini-cart-editor__current');
    const setupLabel = current?.querySelector('span');
    const editLabel = editor.querySelector('.ronini-cart-editor__edit');

    editor.classList.toggle('is-saving', saving);
    editor.toggleAttribute('aria-busy', saving);
    if (saving && nextState) {
      if (setupLabel) setupLabel.textContent = ageLabel(nextState.age, nextState.dueDate);
      if (editLabel) editLabel.textContent = editor.dataset.updatingLabel || 'Updating…';
      details?.removeAttribute('open');
    } else if (editLabel) {
      editLabel.textContent = editLabel.dataset.defaultLabel || 'Edit';
    }
  };

  // After a failed save: reopen the editor with the customer's choices and the message.
  // The cart may have been re-rendered meanwhile (rollback), so the editor is looked up
  // again by kit id in the same cart (drawer or /cart page) and the form is re-applied.
  const keepEditorForm = (editor, inDrawer, kitId, state, message) => {
    const apply = () => {
      const root = inDrawer ? document.querySelector('cart-drawer') : mainCartSection();
      const target = document.contains(editor)
        ? editor
        : root?.querySelector(`ronini-cart-editor[data-kit-id="${CSS.escape(kitId)}"]`);
      if (!target) return;
      const status = target.querySelector('[data-status]');
      if (status?.textContent === message && target.querySelector('details')?.open) return;
      setEditorSavingState(target, false);
      target.querySelector('details')?.setAttribute('open', '');
      target.querySelectorAll('[data-size]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.size === state.size));
      });
      const ageSelect = target.querySelector('[data-age]');
      if (ageSelect) {
        ageSelect.value = state.age;
        ageSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const dueInput = target.querySelector('[data-due-date]');
      const dueValue = target.querySelector('[data-date-value]');
      const due = parseIsoDate(state.dueDate);
      if (dueInput && due) {
        dueInput.value = state.dueDate;
        if (dueValue) {
          dueValue.textContent = displayDate(due);
          dueValue.classList.remove('is-placeholder');
        }
      }
      const currentLabel = target.querySelector('.ronini-cart-editor__current span');
      if (currentLabel) currentLabel.textContent = ageLabel(target.dataset.currentAge || state.age, target.dataset.currentDueDate || '');
      if (status) status.textContent = message;
      const save = target.querySelector('[data-save]');
      if (save) save.disabled = false;
    };
    apply();
    [300, 900, 1800].forEach((delay) => window.setTimeout(apply, delay));
  };

  const NIPPLE_FLOWS = ['drop', 'sip', 'stream', 'rapid'];
  // The spare flow the shopper picked, or '' when it is still the size's suggested default.
  const chosenBonusFlow = (properties) => {
    const flow = String(properties?._bonus_nipple_flow || '').toLowerCase();
    if (!NIPPLE_FLOWS.includes(flow)) return '';
    const size = String(properties?._initial_size || properties?._size || '').replace(/\D/g, '');
    const suggested = sizeMeta[size]?.bonus || '';
    return flow === suggested ? '' : flow;
  };
  // PO02 (review 10-09): the "Preorder" property for the rebuilt parent line, from the TARGET size's state
  // (snippets/rn-preorder via the size button's data-preorder; a Duo also follows its Glass half's state).
  // '' when the target size is in stock, so the property is dropped. ?preorder_preview=1 on an unpublished
  // theme forces the state, as in the buy boxes, so the path can be checked before any stock reaches zero.
  // PO03 (review 10-11): a Duo whose Wrapped and Glass halves are both preorders shows the LATER ship window
  // (an undated "ships in 2–3 weeks" counts as 21 days from today) — never the earlier half's date.
  const poWhen = (date) => { const t = date ? Date.parse(`${date}T12:00:00`) : NaN; return Number.isFinite(t) ? t : Date.now() + 21 * 864e5; };
  const preorderPropertyFor = (editor, selected, size, hasGlass) => {
    let text = String(selected?.dataset.preorder || '').trim();
    const glassText = hasGlass ? String(editor.getAttribute(`data-glass-${size}-preorder`) || '').trim() : '';
    if (glassText && (!text || poWhen(editor.getAttribute(`data-glass-${size}-preorder-date`)) > poWhen(selected?.dataset.preorderDate))) text = glassText;
    if (!text && editor.dataset.preorderPreview === 'true') {
      // preorder_preview=1 forces every size; =160 / =240 forces that size only (both directions checkable).
      try {
        const preview = new URLSearchParams(window.location.search).get('preorder_preview');
        if (preview === '1' || preview === String(size)) text = 'ships in 2–3 weeks';
      } catch (error) { /* no URL access */ }
    }
    return text;
  };
  // Nipple 2-pack variant per flow, from any rendered editor (store-wide ids).
  const nippleVariantFor = (flow, editor) => {
    const source = (editor && editor.dataset.nippleVariants) ? editor
      : document.querySelector('ronini-cart-editor[data-nipple-variants]');
    if (!source || !flow) return 0;
    const pair = String(source.dataset.nippleVariants || '').split(',')
      .map((entry) => entry.split(':'))
      .find(([key]) => key === String(flow).toLowerCase());
    return pair ? Number(pair[1]) || 0 : 0;
  };

  const updatedProperties = (source, size, age, dueDate, kitId, role) => {
    const meta = sizeMeta[size];
    const schedule = fulfillmentSchedule(age, size, dueDate);
    const props = { ...source };
    publicTechnicalProperties.forEach((key) => { delete props[key]; });
    // PO02 (review 10-09): the visible "Preorder" ship window belongs to the target variant, not to the old line.
    // The parent line gets it back from preorderPropertyFor() below; components never carry it.
    delete props.Preorder;
    props._kit_id = kitId;
    props._cart_version = cartVersion;
    props._line_role = role;
    props._entry_age = age;
    props._birth_month = birthMonth(age);
    // R02 (review 10-05): an exact birth date belongs to the 10-month entry it was collected for. Keep it (and the
    // birth month it implies) only while the set stays at 10 months; any other age drops it, so age, birth month,
    // birth date and due date never contradict each other.
    if (age === '10mo' && /^\d{4}-\d{2}-\d{2}$/.test(String(source._birth_date || ''))) {
      props._birth_month = String(source._birth_date).slice(0, 7);
    } else {
      delete props._birth_date;
    }
    props._initial_size = size;
    props._size = meta.size;
    props._initial_flow = meta.flow;
    // R01 (review 10-05): the spare nipple flow is the shopper's choice on the product page. Keep a chosen flow
    // through cart edits; only the untouched suggestion (one flow slower than installed) follows a size change.
    props._bonus_nipple_flow = chosenBonusFlow(source) || meta.bonus;
    props._flow_combination = meta.combination;
    props._ronini_model = meta.model;
    props._next_shipment_in_months = String(schedule.delay);
    props._next_shipment_baby_age_months = String(schedule.babyMonth);
    props._next_shipment_contents = schedule.contents;
    props._recharge_first_charge_delay_months = String(schedule.delay);
    if (props._plan === 'subscription' || props._selling_plan_id) props._refill_flow = schedule.flow;
    else delete props._refill_flow;
    // Retired 09-30 (Box 1 no longer waits for the due date); strip them from
    // lines added before the change.
    delete props._first_ship_target;
    delete props._due_date_short_notice;
    delete props._next_shipment_date;
    if (age === 'expecting' && dueDate) {
      props._due_date = dueDate;
      if (props._refill_flow && schedule.nextShipDate) props._next_shipment_date = schedule.nextShipDate;
    } else {
      delete props._due_date;
    }
    return props;
  };

  const checkoutAddOrder = (items) => {
    const roleRank = {
      wrapped: 10,
      glass: 20,
      welcome_nipple: 80,
      welcome_venting: 90,
      venting: 100,
      addon: 110
    };
    return [...items].sort((a, b) => (
      (roleRank[a.properties?._line_role] || 50) - (roleRank[b.properties?._line_role] || 50)
    ));
  };

  const roleQuantities = (items, kitId) => {
    const quantities = new Map();
    items.forEach((item) => {
      if (item.properties?._kit_id !== kitId) return;
      const role = item.properties?._line_role || 'item';
      quantities.set(role, (quantities.get(role) || 0) + Number(item.quantity || 0));
    });
    return quantities;
  };

  const addWasCommitted = (beforeCart, afterCart, kitId, requestedItems) => {
    const before = roleQuantities(beforeCart.items || [], kitId);
    const after = roleQuantities(afterCart.items || [], kitId);
    const requested = roleQuantities(requestedItems.map((item) => ({
      ...item,
      properties: { ...(item.properties || {}), _kit_id: kitId }
    })), kitId);

    return [...requested.entries()].every(([role, quantity]) => (
      (after.get(role) || 0) >= (before.get(role) || 0) + quantity
    ));
  };

  // Updates that put every role of `kitId` back to its quantity in `beforeCart`,
  // matched by role rather than by (possibly re-hashed) line key.
  const restoreKitUpdates = (beforeCart, currentCart, kitId) => {
    const before = roleQuantities(beforeCart.items || [], kitId);
    const updates = {};
    const assigned = new Set();
    (currentCart.items || []).forEach((item) => {
      if (item.properties?._kit_id !== kitId) return;
      const role = item.properties?._line_role || 'item';
      if (!assigned.has(role)) {
        assigned.add(role);
        const wanted = before.get(role) || 0;
        if (Number(item.quantity || 0) !== wanted) updates[item.key] = wanted;
      } else {
        updates[item.key] = 0;
      }
    });
    return updates;
  };

  const bundledSections = () => {
    const cartSection = mainCartSection();
    if (window.themeVariables?.settings?.pageType === 'cart' && cartSection?.dataset.sectionId) {
      return cartSection.dataset.sectionId;
    }

    const sections = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections }
    }));
    return [...sections].join(',');
  };

  const postJson = async (path, body) => {
    const requestBody = { ...body };
    if (requestBody.sections && !requestBody.sections_url) requestBody.sections_url = window.location.pathname;
    const response = await fetchWithTimeout(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(requestBody)
    }, 15000);
    if (!response.ok) {
      // 422 = Shopify refused the lines (stock, quantity rule); anything else is treated as a connection problem.
      const error = new Error('Cart update failed');
      error.status = response.status;
      const payload = await response.json().catch(() => null);
      error.description = typeof payload?.description === 'string' ? payload.description : '';
      throw error;
    }
    return response.json();
  };

  // theme.js re-renders the drawer from cart.sections. A cart read from
  // /cart.js has no sections, so fetch them before announcing the change —
  // otherwise the drawer keeps showing stale quantities after a revert.
  const withSections = async (cart) => {
    if (!cart || cart.sections) return cart;
    const sectionIds = bundledSections();
    if (!sectionIds) return cart;
    try {
      const response = await fetchWithTimeout(`${window.Shopify?.routes?.root || '/'}?sections=${encodeURIComponent(sectionIds)}`, {
        headers: { Accept: 'application/json' }
      }, 6000);
      if (response.ok) return { ...cart, sections: await response.json() };
    } catch (error) {
      // Fall through: listeners that need sections will refresh on their own.
    }
    return cart;
  };

  const announceCart = async (cart, baseEvent) => {
    const detailCart = await withSections(cart);
    if (!detailCart?.sections) {
      rememberCart(detailCart);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      renderMainCart(detailCart);
      return detailCart;
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart: detailCart }
    }));
    return detailCart;
  };

  const showLineError = (kitId, message) => {
    if (!kitId) return;
    document.querySelectorAll(`line-item[data-ronini-kit-id="${CSS.escape(kitId)}"][data-ronini-kit-parent="true"] .line-item__error-container`).forEach((node) => {
      node.setAttribute('role', 'alert');
      node.textContent = message || '';
    });
  };

  // kitId -> { sets, until }: "Only N available right now." survives re-renders for a few seconds.
  const stockNotices = new Map();
  const showStockNotices = () => {
    const now = Date.now();
    stockNotices.forEach((notice, kitId) => {
      if (notice.until < now) {
        stockNotices.delete(kitId);
        return;
      }
      const line = document.querySelector(`line-item[data-ronini-kit-id="${CSS.escape(kitId)}"][data-ronini-kit-parent="true"]`);
      const template = line?.dataset.roniniStockNotice || 'Only __COUNT__ available right now.';
      showLineError(kitId, template.replace('__COUNT__', String(notice.sets)));
    });
  };

  const quantityErrorMessage = () => document.querySelector('ronini-cart-editor')?.dataset.quantityError
    || 'We couldn’t change the quantity. Your cart still has the amount shown.';

  const rememberPendingReplacement = (replacement) => {
    try {
      window.sessionStorage.setItem(pendingReplacementKey, JSON.stringify(replacement));
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsers. The normal
      // same-request verification below still protects the common path.
    }
  };

  const readPendingReplacement = () => {
    try {
      const value = window.sessionStorage.getItem(pendingReplacementKey);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  };

  const forgetPendingReplacement = () => {
    try {
      window.sessionStorage.removeItem(pendingReplacementKey);
    } catch (error) {
      // No action required when storage is unavailable.
    }
  };

  const cartWithRetries = async () => {
    let lastError;
    for (const delay of [0, 250, 750]) {
      if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
      try {
        return await getCart(true);
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error('Cart verification failed');
  };

  const replacementHasExpectedItems = (cart, replacement) => {
    const actual = roleQuantities(cart.items || [], replacement.newKitId);
    return (replacement.expectedRoles || []).every(([role, quantity]) => (
      (actual.get(role) || 0) >= Number(quantity || 0)
    ));
  };

  const recoverPendingReplacement = async () => {
    const replacement = readPendingReplacement();
    if (!replacement?.oldKitId || !replacement?.newKitId) return null;

    const cart = await cartWithRetries();
    const oldItems = cart.items.filter((item) => item.properties?._kit_id === replacement.oldKitId);
    const newItems = cart.items.filter((item) => item.properties?._kit_id === replacement.newKitId);

    if (!newItems.length) {
      forgetPendingReplacement();
      return cart;
    }

    if (Array.isArray(replacement.inPlace)) {
      // An in-place conversion (convertKitInPlace) was interrupted by a reload.
      const updates = {};
      let live = cart;
      if (replacement.phase !== 'converting') {
        // The added lines were never verified and nothing was converted yet: the old
        // kit is whole, so drop whatever landed for the new one.
        if (!oldItems.length) return cart;
        newItems.forEach((item) => { updates[item.key] = 0; });
      } else {
        // Some lines already carry the new kit: finish the job rather than undo it.
        for (const entry of replacement.inPlace) {
          const line = (live.items || []).find((item) => (
            item.properties?._kit_id === replacement.oldKitId
            && (item.properties?._line_role || 'item') === entry.role
            && Number(item.variant_id) === Number(entry.variantId)
          ));
          if (line) live = await postJson('cart/change.js', { id: line.key, quantity: entry.quantity, properties: entry.properties });
        }
        (live.items || []).forEach((item) => {
          if (item.properties?._kit_id === replacement.oldKitId) updates[item.key] = 0;
        });
      }
      const finishedCart = rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }));
      forgetPendingReplacement();
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'ronini:recover-replacement', cart: finishedCart }
      }));
      return finishedCart;
    }

    const updates = {};
    if (replacementHasExpectedItems(cart, replacement)) {
      oldItems.forEach((item) => { updates[item.key] = 0; });
    } else if (oldItems.length) {
      // A multi-item add should be atomic, but if an app or interrupted request
      // leaves a partial new kit, keep the known-good old kit and remove only
      // the incomplete replacement.
      newItems.forEach((item) => { updates[item.key] = 0; });
    } else {
      // Never delete the customer's only remaining purchase. Keep the marker so
      // a later reload can re-check once Shopify returns a complete cart view.
      return cart;
    }

    const recoveredCart = Object.keys(updates).length
      ? rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }))
      : cart;
    forgetPendingReplacement();
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'ronini:recover-replacement', cart: recoveredCart }
    }));
    return recoveredCart;
  };

  const migrateLegacyCart = async (providedCart) => {
    const cart = providedCart?.items ? providedCart : await getCart();
    const kitGroups = new Map();

    // TID01-legacy (review 10-09): carts saved before the kit id carried the exact birth date / gift flow can hold
    // two sets under ONE kit id that differ in those properties. They are migrated as separate sets (the second
    // one gets its own kit id) instead of being summed into one line that keeps only the first set's birth date.
    const identityOf = (main) => `${main.properties?._birth_date || ''}|${main.properties?._bonus_nipple_flow || ''}`;
    const identitySuffix = (main) => identityOf(main).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
    cart.items.filter((item) => (
      item.properties?._line_role === 'wrapped'
      && item.properties?._kit_parent === 'true'
      && (item.selling_plan_allocation || item.properties?._plan === 'subscription')
    )).forEach((main) => {
      const kitId = main.properties?._kit_id;
      if (!kitId) return;
      if (!kitGroups.has(kitId)) kitGroups.set(kitId, new Map());
      const byIdentity = kitGroups.get(kitId);
      const identity = identityOf(main);
      if (!byIdentity.has(identity)) byIdentity.set(identity, []);
      byIdentity.get(identity).push(main);
    });

    const migrations = [];
    kitGroups.forEach((byIdentity, kitId) => {
      const split = byIdentity.size > 1;
      let index = 0;
      byIdentity.forEach((mains) => {
        const needsMigration = split || mains.some((main) => (
          main.properties?._cart_version !== cartVersion
          || !main.properties?.['Next shipment']
        ));
        index += 1;
        if (!needsMigration) return;

        const main = mains[0];
        const targetKitId = (split && index > 1) ? `${kitId}-${identitySuffix(main) || index}` : kitId;
        const props = { ...main.properties, _cart_version: cartVersion, _kit_id: targetKitId };
        const size = String(props._initial_size || props.initial_size || '160').replace(/\D/g, '') || '160';
        const age = props._entry_age || props.baby_stage || '0mo';
        const editor = document.querySelector(`ronini-cart-editor[data-kit-id="${CSS.escape(kitId)}"]`);
        props['Next shipment'] = forSet(props, nextShipmentLabel(editor, fulfillmentSchedule(age, size, props._due_date || props.due_date)));
        if (main.selling_plan_allocation) setUpgradeNote(props, age, size, props._due_date || props.due_date);

        const item = {
          id: Number(main.variant_id),
          quantity: mains.reduce((sum, line) => sum + Number(line.quantity || 0), 0),
          properties: props
        };
        const planId = main.selling_plan_allocation?.selling_plan?.id;
        if (planId) item.selling_plan = Number(planId);
        migrations.push({ kitId: targetKitId, mains, item });
      });
    });

    if (!migrations.length) return cart;

    const lockKey = 'ronini-cart-migration-v4';
    try {
      const lockTime = Number(localStorage.getItem(lockKey) || 0);
      if (Date.now() - lockTime < 15000) return cart;
      localStorage.setItem(lockKey, String(Date.now()));
    } catch (error) {
    }

    await postJson('cart/add.js', { items: checkoutAddOrder(migrations.map((migration) => migration.item)) });
    const updates = {};
    migrations.forEach((migration) => migration.mains.forEach((main) => { updates[main.key] = 0; }));

    try {
      const updatedCart = rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'ronini:migrate-cart', cart: updatedCart }
      }));
      return updatedCart;
    } catch (migrationError) {
      const recoveryCart = await getCart(true);
      const oldCartIsIntact = migrations.every((migration) => (
        migration.mains.every((main) => recoveryCart.items.some((item) => item.key === main.key))
      ));
      if (!oldCartIsIntact) {
        rememberCart(recoveryCart);
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'ronini:migrate-cart-recovery', cart: recoveryCart }
        }));
        return recoveryCart;
      }

      if (oldCartIsIntact) {
        const migratedKitIds = new Set(migrations.map((migration) => migration.kitId));
        const rollbackUpdates = {};
        recoveryCart.items.forEach((item) => {
          if (migratedKitIds.has(item.properties?._kit_id) && item.properties?._cart_version === cartVersion) {
            rollbackUpdates[item.key] = 0;
          }
        });
        if (Object.keys(rollbackUpdates).length) await postJson('cart/update.js', { updates: rollbackUpdates });
      }
      throw migrationError;
    } finally {
      try {
        localStorage.removeItem(lockKey);
      } catch (error) {
      }
    }
  };

  // One-time Duo = the fixed Shopify bundle (owner 2026-10-02). Config from
  // snippets/rn-duo-bundles.liquid, rendered next to the cart state.
  let duoBundles = {};
  try {
    duoBundles = JSON.parse(document.getElementById('ronini-duo-bundles')?.textContent || '{}') || {};
  } catch (error) {
    duoBundles = {};
  }

  // A cart from before the bundle still holds a one-time Duo as a Wrapped line + a
  // Glass line (one kit). Swap each such pair for the bundle line with the same
  // set quantity. The swap adds first and removes after, so a failure never
  // leaves the customer without their Duo; a pair that cannot be swapped (bundle
  // out of stock, uneven quantities) stays as it is and still renders as a Duo.
  const migrateLegacyOneTimeDuos = async (providedCart) => {
    const cart = providedCart?.items ? providedCart : await getCart();
    const kits = new Map();
    cart.items.forEach((item) => {
      const props = item.properties || {};
      if (!props._kit_id) return;
      const entry = kits.get(props._kit_id) || { kitId: props._kit_id, parents: [], glass: [], venting: [], other: 0, subscription: false };
      if (item.selling_plan_allocation || props._plan === 'subscription') entry.subscription = true;
      if (props._line_role === 'wrapped' && props._kit_parent === 'true') entry.parents.push(item);
      else if (props._line_role === 'glass') entry.glass.push(item);
      else if (props._line_role === 'venting') entry.venting.push(item);
      else entry.other += 1;
      kits.set(props._kit_id, entry);
    });

    const sum = (lines) => lines.reduce((total, line) => total + Number(line.quantity || 0), 0);
    const swaps = [];
    kits.forEach((kit) => {
      if (kit.subscription || kit.other || !kit.parents.length || !kit.glass.length) return;
      const size = String(kit.parents[0].properties?._initial_size || kit.parents[0].properties?._size || '').replace(/\D/g, '');
      const bundle = duoBundles[size];
      const sets = sum(kit.parents);
      if (!bundle?.variantId || bundle.available === false || sets < 1 || sets !== sum(kit.glass)) return;
      if (bundle.max !== null && bundle.max !== undefined && sets > Number(bundle.max)) return;
      swaps.push({ kit, size, bundle, sets });
    });
    if (!swaps.length) return cart;

    const lockKey = 'ronini-cart-duo-bundle-migration';
    try {
      if (Date.now() - Number(localStorage.getItem(lockKey) || 0) < 15000) return cart;
      localStorage.setItem(lockKey, String(Date.now()));
    } catch (error) {
    }

    try {
      const additions = [];
      swaps.forEach(({ kit, size, bundle, sets }) => {
        additions.push({ id: Number(bundle.variantId), quantity: sets, properties: {
          _line_role: 'duo_bundle', _plan: 'onetime', _initial_size: size, _size: `${size}ml`
        } });
        // A paid venting add-on stays in the cart as an ordinary line of its own.
        kit.venting.forEach((line) => additions.push({ id: Number(line.variant_id), quantity: Number(line.quantity || 0) }));
      });
      const beforeBundle = (variantId) => cart.items.reduce((total, line) => (
        Number(line.variant_id) === Number(variantId) ? total + Number(line.quantity || 0) : total
      ), 0);
      await postJson('cart/add.js', { items: additions });

      const afterAdd = await cartWithRetries();
      const added = swaps.every(({ bundle, sets }) => afterAdd.items.reduce((total, line) => (
        Number(line.variant_id) === Number(bundle.variantId) ? total + Number(line.quantity || 0) : total
      ), 0) >= beforeBundle(bundle.variantId) + sets);
      if (!added) return afterAdd;

      const kitIds = new Set(swaps.map((swap) => swap.kit.kitId));
      const updates = {};
      afterAdd.items.forEach((line) => {
        if (kitIds.has(line.properties?._kit_id)) updates[line.key] = 0;
      });
      const updatedCart = rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'ronini:migrate-cart', cart: updatedCart }
      }));
      return updatedCart;
    } finally {
      try {
        localStorage.removeItem(lockKey);
      } catch (error) {
      }
    }
  };

  const canonicalKitId = (main, kitItems, overrides = {}) => [
    'ronini',
    overrides.productId || main.product_id,
    overrides.size || main.properties?._initial_size || main.properties?.initial_size,
    overrides.planId || main.selling_plan_allocation?.selling_plan?.id || 'onetime',
    overrides.age || main.properties?._entry_age || main.properties?.baby_stage,
    overrides.birthMonth || main.properties?._birth_month || main.properties?.birth_month || 'no-birth-month',
    Object.prototype.hasOwnProperty.call(overrides, 'dueDate')
      ? (overrides.dueDate || 'no-due-date')
      : (main.properties?._due_date || main.properties?.due_date || 'no-due-date'),
    main.properties?._duo_flag === 'true' ? 'duo' : 'single',
    kitItems.some((item) => item.properties?._line_role === 'venting') ? 'vent' : 'no-vent'
  ].join('-').replace(/[^a-z0-9_-]+/gi, '-').toLowerCase();

  const stableKitId = (editor, selected, age, dueDate, main, oldItems) => canonicalKitId(main, oldItems, {
    productId: editor.dataset.productId,
    size: selected.dataset.size,
    planId: editor.dataset.subscription === 'true' ? selected.dataset.planId : 'onetime',
    age,
    dueDate,
    birthMonth: birthMonth(age) || 'no-birth-month'
  });

  const purchaseItemMode = (item) => item.selling_plan_allocation || item.properties?._plan === 'subscription' ? 'subscription' : 'onetime';
  const purchaseItemSignature = (item) => item.properties?._line_role || 'item';
  // Welcome kits per subscribed set: 1, for a Wrapped set and for a Duo set alike
  // (client 2026-10-01 — reverses the 09-30 "2 per Duo set").
  const giftsPerSet = () => 1;
  const isWelcomeGift = (item) => ['welcome_venting', 'welcome_nipple'].includes(item.properties?._line_role)
    || item.properties?._kit_price_mode === 'subscription_gift';
  // 10-13 (owner): the early access Venting Tube is ONE free 2-pack per order, not one per set.
  const isLaunchTube = (item) => item?.properties?._line_role === 'welcome_venting'
    && item?.properties?._kit_price_mode === 'launch_gift';
  const giftQuantityFor = (target, role) => (target.roleQuantity && target.roleQuantity[role] !== undefined
    ? target.roleQuantity[role]
    : target.giftQuantity);
  const isSubscriptionParent = (item) => item.properties?._line_role === 'wrapped'
    && item.properties?._kit_parent === 'true'
    && !!(item.selling_plan_allocation || item.properties?._plan === 'subscription');
  // Every component (Glass, paid venting, welcome gifts) follows the set
  // quantity: welcome gifts = sets × 1 for a Wrapped set and a Duo set.
  const packageComponentQuantity = (role, packageQuantity, parent) => Math.max(0, packageQuantity)
    * (role === 'welcome_venting' || role === 'welcome_nipple' ? giftsPerSet(parent) : 1);

  const packageGroupForLine = (cart, lineKey, kitId = '') => {
    // Line keys change whenever Shopify reallocates a discount, so a key rendered
    // a moment ago can already be stale. Fall back to the package identity.
    const clickedParent = cart.items.find((item) => item.key === lineKey)
      || (kitId && cart.items.find((item) => (
        item.properties?._kit_id === kitId
        && item.properties?._line_role === 'wrapped'
        && item.properties?._kit_parent === 'true'
      )));
    if (!clickedParent) return null;
    const clickedKitId = clickedParent.properties?._kit_id;
    // Never merge server lines from different kit IDs in-place. Shopify cannot
    // change line-item properties through cart/update.js, so adopting a child
    // from another kit would leave it attached to a removed parent.
    const items = cart.items.filter((item) => item.properties?._kit_id === clickedKitId);
    const parents = items.filter((item) => item.properties?._line_role === 'wrapped' && item.properties?._kit_parent === 'true');
    return {
      clickedParent,
      parents,
      items,
      totalQuantity: parents.reduce((sum, parent) => sum + Number(parent.quantity || 0), 0)
    };
  };

  const packageQuantityUpdates = (group, requestedLineQuantity, removeAll = false) => {
    const targetQuantity = removeAll ? 0 : Math.max(0, requestedLineQuantity);
    const targetKitId = group.clickedParent.properties?._kit_id;
    const itemsByRole = new Map();

    group.items.forEach((item) => {
      const role = purchaseItemSignature(item);
      if (!itemsByRole.has(role)) itemsByRole.set(role, []);
      itemsByRole.get(role).push(item);
    });

    const updates = {};
    itemsByRole.forEach((items, role) => {
      const target = role === 'wrapped'
        ? group.clickedParent
        : items.find((item) => item.properties?._kit_id === targetKitId) || items[0];
      updates[target.key] = isLaunchTube(target)
        ? Math.min(1, packageComponentQuantity(role, targetQuantity, group.clickedParent))
        : packageComponentQuantity(role, targetQuantity, group.clickedParent);
      items.forEach((item) => {
        if (item.key !== target.key) updates[item.key] = 0;
      });
    });
    return updates;
  };

  const showOptimisticPackageQuantity = (lineItem, quantity) => {
    const selector = lineItem?.querySelector('quantity-selector');
    const input = selector?.querySelector('.quantity-selector__input');
    if (!selector || !input) return;

    const step = Number(input.step) || 1;
    const minimum = Number(input.min) || 1;
    const maximum = input.max ? Number(input.max) : Infinity;
    const nextQuantity = Math.max(minimum, Math.min(maximum, quantity));
    const buttons = selector.querySelectorAll('[data-ronini-package-quantity]');

    input.value = String(nextQuantity);
    if (buttons[0]) {
      buttons[0].dataset.roniniPackageQuantity = String(nextQuantity - step);
      buttons[0].disabled = nextQuantity <= minimum;
    }
    if (buttons[1]) {
      buttons[1].dataset.roniniPackageQuantity = String(nextQuantity + step);
      buttons[1].disabled = nextQuantity >= maximum;
    }
    lineItem.dataset.roniniDisplayQuantity = String(nextQuantity);
    lineItem.closest('.rnc-item, .ronini-cart-kit')?.classList.add('is-optimistic');
  };

  const reapplyPendingPackageQuantities = () => {
    pendingPackageQuantities.forEach((entry) => {
      const lineItem = document.querySelector(`line-item[data-ronini-kit-id="${CSS.escape(entry.kitId)}"][data-ronini-kit-parent="true"]`);
      if (!lineItem) return;
      if (entry.removing) {
        setPackageMutationState(lineItem, true, true);
      } else {
        showOptimisticPackageQuantity(lineItem, entry.quantity);
      }
    });
  };

  const flushPackageQuantities = async () => {
    packageQuantityTimer = null;
    if (packageQuantityRequestRunning || !pendingPackageQuantities.size) return;

    packageQuantityRequestRunning = true;
    const batch = [...pendingPackageQuantities.values()];
    batch.forEach((entry) => pendingPackageQuantities.delete(entry.kitId));

    try {
      await waitForGiftPass();
      const cart = await getCart(true);
      const updates = {};
      batch.forEach((entry) => {
        const group = packageGroupForLine(cart, entry.lineKey, entry.kitId);
        if (!group) return;
        Object.assign(updates, packageQuantityUpdates(group, entry.quantity, entry.removing));
      });
      if (!Object.keys(updates).length) {
        await announceCart(cart, 'ronini:bundle-quantity-revert');
        return;
      }

      const updatedCart = rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }));
      // Shopify caps a line at the stock it has without an error. Tell the
      // customer; the reconcile pass brings the set's Glass and gifts back to it.
      batch.forEach((entry) => {
        if (entry.removing) return;
        const sets = (updatedCart.items || []).reduce((sum, item) => (
          item.properties?._kit_id === entry.kitId && item.properties?._line_role === 'wrapped' && item.properties?._kit_parent === 'true'
            ? sum + Number(item.quantity || 0) : sum
        ), 0);
        if (sets > 0 && sets < entry.quantity) stockNotices.set(entry.kitId, { sets, until: Date.now() + 8000 });
      });
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'ronini:bundle-quantity', cart: updatedCart }
      }));
      reapplyPendingPackageQuantities();
      showStockNotices();
    } catch (error) {
      currentCart = null;
      try {
        const confirmedCart = rememberCart(await getCart(true));
        await announceCart(confirmedCart, 'ronini:bundle-quantity-revert');
      } catch (refreshError) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (window.themeVariables?.settings?.pageType === 'cart') window.location.reload();
      }
      // Re-rendering replaces the line items, so write the message afterwards.
      batch.forEach((entry) => showLineError(entry.kitId, quantityErrorMessage()));
    } finally {
      batch.forEach((entry) => setPackageMutationState(entry.lineItem, false));
      packageQuantityRequestRunning = false;

      if (pendingPackageQuantities.size) {
        packageQuantityTimer = setTimeout(flushPackageQuantities, 60);
      } else {
        finishUserMutation();
      }
    }
  };

  const queuePackageQuantity = (entry, immediate = false) => {
    pendingPackageQuantities.set(entry.kitId, entry);
    userMutationRunning = true;
    clearTimeout(packageQuantityTimer);
    packageQuantityTimer = setTimeout(flushPackageQuantities, immediate ? 0 : 70);
  };

  const datePickerState = new WeakMap();
  const calendarToday = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  };
  const parseIsoDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const toIsoDate = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
  const displayDate = (date) => `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  const sameDay = (left, right) => left && right
    && left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
  const datePickerLocale = document.documentElement.lang || 'en-US';

  const pickerState = (editor) => {
    if (datePickerState.has(editor)) return datePickerState.get(editor);
    const input = editor.querySelector('[data-due-date]');
    const selected = parseIsoDate(input?.value);
    const today = calendarToday();
    const initial = selected || new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const state = { month: new Date(initial.getFullYear(), initial.getMonth(), 1) };
    datePickerState.set(editor, state);
    return state;
  };

  const renderDatePicker = (editor, moveFocus = false) => {
    const state = pickerState(editor);
    const input = editor.querySelector('[data-due-date]');
    const selected = parseIsoDate(input?.value);
    const today = calendarToday();
    const month = state.month;
    const monthLabel = editor.querySelector('[data-date-month]');
    const previousButton = editor.querySelector('[data-date-prev]');
    const grid = editor.querySelector('[data-date-grid]');
    if (!monthLabel || !previousButton || !grid) return;

    monthLabel.textContent = month.toLocaleDateString(datePickerLocale, { month: 'long', year: 'numeric' });
    previousButton.disabled = month.getFullYear() === today.getFullYear() && month.getMonth() <= today.getMonth();

    const firstWeekday = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    // Roving tabindex target (keyboard): the kept focus day, else the selected day, else today, else the 1st.
    const inMonth = (date) => date && date.getFullYear() === month.getFullYear() && date.getMonth() === month.getMonth() && date >= today;
    const focusDay = [state.focus, selected, today, new Date(month.getFullYear(), month.getMonth(), 1)].find(inMonth)
      || new Date(month.getFullYear(), month.getMonth(), 1);
    state.focus = focusDay;
    for (let index = 0; index < firstWeekday; index += 1) cells.push('<td aria-hidden="true"></td>');
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const isPast = date < today;
      const isSelected = sameDay(date, selected);
      const isToday = sameDay(date, today);
      const classes = [isSelected ? 'is-selected' : '', isToday ? 'is-today' : ''].filter(Boolean).join(' ');
      const label = date.toLocaleDateString(datePickerLocale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).replace(/"/g, '&quot;');
      const isFocus = sameDay(date, focusDay);
      cells.push(`<td class="${classes}"><button type="button" data-date-day="${toIsoDate(date)}" aria-label="${label}" aria-pressed="${isSelected}"${isToday ? ' aria-current="date"' : ''}${isPast ? ' disabled' : ''} tabindex="${isFocus ? '0' : '-1'}">${day}</button></td>`);
    }
    while (cells.length % 7) cells.push('<td aria-hidden="true"></td>');
    grid.innerHTML = cells.reduce((rows, cell, index) => `${rows}${index % 7 === 0 ? '<tr>' : ''}${cell}${index % 7 === 6 ? '</tr>' : ''}`, '');
    // Done confirms a picked date, so it stays disabled until there is one.
    const done = editor.querySelector('[data-date-done]');
    if (done) done.disabled = !selected;
    if (moveFocus) grid.querySelector('button[tabindex="0"]')?.focus({ preventScroll: true });
  };

  const closeDatePicker = (editor, restoreFocus = false) => {
    const panel = editor?.querySelector('[data-date-panel]');
    const toggle = editor?.querySelector('[data-date-toggle]');
    if (!panel || !toggle || panel.hidden) return;
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle.focus();
  };

  const closeOtherDatePickers = (activeEditor) => {
    document.querySelectorAll('ronini-cart-editor').forEach((editor) => {
      if (editor !== activeEditor) closeDatePicker(editor);
    });
  };

  document.addEventListener('click', (event) => {
    const quantityControl = event.target.closest('line-item[data-ronini-kit-parent="true"] [data-ronini-package-quantity], line-item[data-ronini-kit-parent="true"] [data-ronini-package-remove], line-item[data-ronini-kit-parent="true"] a[href*="cart/change"]');
    if (!quantityControl) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const lineItem = quantityControl.closest('line-item');
    const kitId = lineItem.dataset.roniniKitId;
    const targetUrl = quantityControl.href ? new URL(quantityControl.href, window.location.origin) : null;
    const lineKey = quantityControl.dataset.lineKey || targetUrl?.searchParams.get('id');
    const isRemoving = quantityControl.hasAttribute('data-ronini-package-remove');
    const input = lineItem.querySelector('.quantity-selector__input');
    const currentQuantity = Number(input?.value || lineItem.dataset.roniniDisplayQuantity || 1);
    const requestedQuantity = Number(quantityControl.dataset.roniniPackageQuantity ?? targetUrl?.searchParams.get('quantity'));
    const direction = requestedQuantity < currentQuantity ? -1 : 1;
    const step = Number(input?.step) || 1;
    const minimum = Number(input?.min) || 1;
    const maximum = input?.max ? Number(input.max) : Infinity;
    const quantity = isRemoving
      ? 0
      : Math.max(minimum, Math.min(maximum, currentQuantity + direction * step));
    if (!kitId || !lineKey || !Number.isFinite(quantity)) return;
    if (!isRemoving && quantity === currentQuantity) return;
    showLineError(kitId, '');

    if (isRemoving) {
      setPackageMutationState(lineItem, true, true);
    } else {
      showOptimisticPackageQuantity(lineItem, quantity);
    }
    queuePackageQuantity({ kitId, lineKey, lineItem, quantity, removing: isRemoving }, isRemoving);
  }, true);

  document.addEventListener('change', (event) => {
    const quantityInput = event.target.closest('line-item[data-ronini-kit-parent="true"] [data-line-key]');
    if (!quantityInput) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const lineItem = quantityInput.closest('line-item');
    const kitId = lineItem.dataset.roniniKitId;
    const lineKey = quantityInput.dataset.lineKey;
    const minimum = Math.max(1, Number(quantityInput.min) || 1);
    const maximum = quantityInput.max ? Number(quantityInput.max) : Infinity;
    const rawValue = String(quantityInput.value).trim();
    const parsedQuantity = /^\d+$/.test(rawValue) ? Number(rawValue) : NaN;
    const confirmedQuantity = lineItem.dataset.roniniDisplayQuantity || String(minimum);
    if (!kitId || !lineKey || !Number.isInteger(parsedQuantity) || parsedQuantity < minimum || parsedQuantity > maximum) {
      quantityInput.value = confirmedQuantity;
      quantityInput.setAttribute('aria-invalid', 'true');
      const message = parsedQuantity === 0
        ? 'To remove this set, use Remove.'
        : (Number.isFinite(maximum) ? `Enter a whole number from ${minimum} to ${maximum}.` : `Enter a whole number of ${minimum} or more.`);
      showLineError(kitId, message);
      return;
    }
    quantityInput.removeAttribute('aria-invalid');
    showLineError(kitId, '');
    if (String(parsedQuantity) === String(confirmedQuantity)) return;
    showOptimisticPackageQuantity(lineItem, parsedQuantity);
    queuePackageQuantity({ kitId, lineKey, lineItem, quantity: parsedQuantity, removing: false });
  }, true);

  document.addEventListener('change', (event) => {
    const ageSelect = event.target.closest('ronini-cart-editor [data-age]');
    if (!ageSelect) return;
    const editor = ageSelect.closest('ronini-cart-editor');
    const dueDateField = editor.querySelector('[data-due-date-field]');
    const dueDateInput = editor.querySelector('[data-due-date]');
    const isExpecting = ageSelect.value === 'expecting';
    if (!dueDateField || !dueDateInput) return;
    dueDateField.hidden = !isExpecting;
    dueDateInput.required = isExpecting;
    if (!isExpecting) closeDatePicker(editor);
  });

  document.addEventListener('click', (event) => {
    const editor = event.target.closest('ronini-cart-editor');
    const dateControl = event.target.closest('[data-date-toggle], [data-date-prev], [data-date-next], [data-date-day], [data-date-today], [data-date-done]');

    if (!editor || !dateControl) {
      if (!event.target.closest('[data-date-picker]')) closeOtherDatePickers(null);
      return;
    }

    const panel = editor.querySelector('[data-date-panel]');
    const toggle = editor.querySelector('[data-date-toggle]');
    const state = pickerState(editor);
    const input = editor.querySelector('[data-due-date]');
    const value = editor.querySelector('[data-date-value]');

    if (dateControl.matches('[data-date-toggle]')) {
      const willOpen = panel.hidden;
      closeOtherDatePickers(editor);
      panel.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) renderDatePicker(editor);
      return;
    }

    if (dateControl.matches('[data-date-prev]')) state.month.setMonth(state.month.getMonth() - 1);
    if (dateControl.matches('[data-date-next]')) state.month.setMonth(state.month.getMonth() + 1);

    if (dateControl.matches('[data-date-day], [data-date-today]')) {
      const selected = dateControl.matches('[data-date-today]')
        ? calendarToday()
        : parseIsoDate(dateControl.dataset.dateDay);
      if (selected) {
        input.value = toIsoDate(selected);
        value.textContent = displayDate(selected);
        value.classList.remove('is-placeholder');
        state.month = new Date(selected.getFullYear(), selected.getMonth(), 1);
      }
    }

    if (dateControl.matches('[data-date-done]')) {
      closeDatePicker(editor, true);
      return;
    }

    if (dateControl.matches('[data-date-day]')) state.focus = parseIsoDate(dateControl.dataset.dateDay);
    if (dateControl.matches('[data-date-today]')) state.focus = calendarToday();
    renderDatePicker(editor, dateControl.matches('[data-date-day]') && document.activeElement === dateControl);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const editor = event.target.closest('ronini-cart-editor');
    if (editor) closeDatePicker(editor, true);
  });

  // Calendar grid keyboard: arrows = ±1 day / ±1 week, Home/End = week start/end, PageUp/PageDown = ±1 month.
  document.addEventListener('keydown', (event) => {
    const button = event.target.closest?.('ronini-cart-editor [data-date-day]');
    if (!button) return;
    const from = parseIsoDate(button.dataset.dateDay);
    if (!from) return;
    const next = new Date(from.getTime());
    switch (event.key) {
      case 'ArrowLeft': next.setDate(from.getDate() - 1); break;
      case 'ArrowRight': next.setDate(from.getDate() + 1); break;
      case 'ArrowUp': next.setDate(from.getDate() - 7); break;
      case 'ArrowDown': next.setDate(from.getDate() + 7); break;
      case 'Home': next.setDate(from.getDate() - from.getDay()); break;
      case 'End': next.setDate(from.getDate() + (6 - from.getDay())); break;
      case 'PageUp': next.setMonth(from.getMonth() - 1); break;
      case 'PageDown': next.setMonth(from.getMonth() + 1); break;
      default: return;
    }
    event.preventDefault();
    const editor = button.closest('ronini-cart-editor');
    const state = pickerState(editor);
    const today = calendarToday();
    const target = next < today ? today : next;
    state.focus = target;
    state.month = new Date(target.getFullYear(), target.getMonth(), 1);
    renderDatePicker(editor, true);
  });

  // Swap one kit for a rebuilt one (the editor's Save, and "Make it a Duo" on a
  // subscription set): add the new kit's lines first, verify them, then remove the
  // old kit by its kit id, rolling back if the swap cannot complete. Returns the
  // updated cart (already announced), or null when the page is reloading.
  const lineRole = (entry) => entry.properties?._line_role || 'item';

  // The rebuilt kit keeps the set's own Wrapped variant (an age / due-date edit, or
  // "Make it a Duo"): rewrite the properties of the lines that stay (cart/change.js)
  // instead of adding the whole kit a second time. Adding first needs twice the stock
  // while both copies are in the cart, so Shopify refused it with 422 as soon as
  // sets × 2 was more than the variant's stock (9 sets, 10 in stock) and every such
  // edit ended in "Could not update". Lines of a new variant are still added first;
  // old lines the new kit no longer has are removed last.
  const convertKitInPlace = async (cart, oldKitId, newKitId, orderedItems, matches, baseEvent) => {
    const inPlace = [];
    const additions = [];
    orderedItems.forEach((item) => {
      const line = matches.get(item);
      if (!line) {
        additions.push(item);
        return;
      }
      inPlace.push({
        role: lineRole(item),
        variantId: Number(item.id),
        quantity: Number(item.quantity),
        properties: item.properties,
        previous: { quantity: Number(line.quantity), properties: line.properties }
      });
    });
    // The parent goes last: until then every other cart pass still sees the old set.
    inPlace.sort((a, b) => Number(a.role === 'wrapped') - Number(b.role === 'wrapped'));

    const findLine = (source, kitId, entry) => (source.items || []).find((line) => (
      line.properties?._kit_id === kitId && lineRole(line) === entry.role && Number(line.variant_id) === entry.variantId
    ));
    const newKitRemovals = (source) => {
      const updates = {};
      (source.items || []).forEach((line) => {
        if (line.properties?._kit_id === newKitId) updates[line.key] = 0;
      });
      return updates;
    };
    const marker = {
      oldKitId,
      newKitId,
      phase: 'adding',
      expectedRoles: [...roleQuantities(additions.map((item) => ({
        ...item,
        properties: { ...(item.properties || {}), _kit_id: newKitId }
      })), newKitId).entries()],
      inPlace: inPlace.map(({ role, variantId, quantity, properties }) => ({ role, variantId, quantity, properties }))
    };
    rememberPendingReplacement(marker);

    if (additions.length) {
      try {
        await postJson('cart/add.js', { items: additions });
      } catch (addError) {
        const recoveryCart = await cartWithRetries();
        if (!addWasCommitted(cart, recoveryCart, newKitId, additions)) {
          // Shopify can add part of a request ("Only 1 item was added…"): take it out again.
          const cleanup = newKitRemovals(recoveryCart);
          if (Object.keys(cleanup).length) await postJson('cart/update.js', { updates: cleanup });
          forgetPendingReplacement();
          throw addError;
        }
      }
      // SF03: a 200 can still be partial — check every added role before converting the old lines.
      const afterAdd = await cartWithRetries();
      if (!addWasCommitted(cart, afterAdd, newKitId, additions)) {
        const cleanup = newKitRemovals(afterAdd);
        if (Object.keys(cleanup).length) await postJson('cart/update.js', { updates: cleanup });
        forgetPendingReplacement();
        throw new Error('Part of the new set could not be added, so your current set was kept.');
      }
    }

    rememberPendingReplacement({ ...marker, phase: 'converting' });
    let live = await cartWithRetries();
    try {
      for (const entry of inPlace) {
        const line = findLine(live, oldKitId, entry);
        if (!line) throw new Error('Set line missing');
        // Quantity is required: without it cart/change.js resets the line to 1.
        live = await postJson('cart/change.js', { id: line.key, quantity: entry.quantity, properties: entry.properties });
      }
    } catch (convertError) {
      try {
        // Put every converted line back, then take the added lines out again.
        let recoveryCart = await cartWithRetries();
        for (const entry of [...inPlace].reverse()) {
          const line = findLine(recoveryCart, newKitId, entry);
          if (line) {
            recoveryCart = await postJson('cart/change.js', {
              id: line.key,
              quantity: entry.previous.quantity,
              properties: entry.previous.properties
            });
          }
        }
        const restoredCart = rememberCart(await postJson('cart/update.js', {
          updates: newKitRemovals(recoveryCart),
          sections: bundledSections()
        }));
        forgetPendingReplacement();
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'ronini:edit-rollback', cart: restoredCart }
        }));
      } catch (rollbackError) {
        // Still offline: the marker stays, and the next page load finishes the conversion.
        currentCart = null;
      }
      throw convertError;
    }

    const leftovers = {};
    (live.items || []).forEach((line) => {
      if (line.properties?._kit_id === oldKitId) leftovers[line.key] = 0;
    });
    let updatedCart;
    try {
      updatedCart = rememberCart(await postJson('cart/update.js', { updates: leftovers, sections: bundledSections() }));
    } catch (finishError) {
      // The set is converted; only the re-render (and any leftover line) is outstanding.
      // The marker stays so the reload completes it.
      currentCart = null;
      window.location.reload();
      return null;
    }
    forgetPendingReplacement();
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart: updatedCart }
    }));
    return updatedCart;
  };

  const replaceKit = async (cart, oldKitId, newKitId, items, baseEvent) => {
    const orderedItems = checkoutAddOrder(items);

    // Which lines of the rebuilt kit are lines the old kit already has (same role,
    // variant and selling plan)?
    const oldLines = (cart.items || []).filter((line) => line.properties?._kit_id === oldKitId);
    const claimed = new Set();
    const matches = new Map();
    orderedItems.forEach((item) => {
      const line = oldLines.find((candidate) => (
        !claimed.has(candidate.key)
        && Number(candidate.variant_id) === Number(item.id)
        && lineRole(candidate) === lineRole(item)
        && Number(candidate.selling_plan_allocation?.selling_plan?.id || 0) === Number(item.selling_plan || 0)
      ));
      if (!line) return;
      claimed.add(line.key);
      matches.set(item, line);
    });
    const parentItem = orderedItems.find((item) => lineRole(item) === 'wrapped');
    if (parentItem && matches.has(parentItem)) {
      return convertKitInPlace(cart, oldKitId, newKitId, orderedItems, matches, baseEvent);
    }

    rememberPendingReplacement({
      oldKitId,
      newKitId,
      expectedRoles: [...roleQuantities(orderedItems.map((item) => ({
        ...item,
        properties: { ...(item.properties || {}), _kit_id: newKitId }
      })), newKitId).entries()]
    });
    let addedPayload;
    try {
      addedPayload = await postJson('cart/add.js', { items: orderedItems });
    } catch (addError) {
      const recoveryCart = await cartWithRetries();
      if (!addWasCommitted(cart, recoveryCart, newKitId, orderedItems)) {
        const cleanupUpdates = restoreKitUpdates(cart, recoveryCart, newKitId);
        if (Object.keys(cleanupUpdates).length) {
          await postJson('cart/update.js', { updates: cleanupUpdates, sections: bundledSections() });
        }
        forgetPendingReplacement();
        throw addError;
      }
      addedPayload = {
        items: recoveryCart.items.filter((item) => item.properties?._kit_id === newKitId)
      };
    }
    // Keys of the old lines change when the add reallocates discounts (the Duo
    // discount does), and a stale key can make Shopify fall back to the first
    // line of that variant — i.e. zero the NEW Glass. Re-read the cart and
    // address the old package by its kit id instead (CART10 regression).
    const afterAddCart = await cartWithRetries();
    // Review 2026-10-03 SF03: a 200 from cart/add does not prove every role arrived (Shopify can cap or drop a
    // line). Only remove the old set once the new one is complete; otherwise take the partial new set back out
    // and keep the old set untouched.
    if (!addWasCommitted(cart, afterAddCart, newKitId, orderedItems)) {
      const partialUpdates = restoreKitUpdates(cart, afterAddCart, newKitId);
      if (Object.keys(partialUpdates).length) {
        const restoredCart = rememberCart(await postJson('cart/update.js', { updates: partialUpdates, sections: bundledSections() }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'ronini:edit-rollback', cart: restoredCart }
        }));
      }
      forgetPendingReplacement();
      throw new Error('Part of the new set could not be added, so your current set was kept.');
    }
    const updates = {};
    afterAddCart.items.forEach((item) => {
      if (item.properties?._kit_id === oldKitId) updates[item.key] = 0;
    });
    let updatedCart;

    try {
      updatedCart = rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }));
    } catch (replaceError) {
      const recoveryCart = await getCart(true);
      const oldPackageIsIntact = recoveryCart.items.some((item) => item.properties?._kit_id === oldKitId);

      // The server can finish the update even if the response is interrupted.
      // If the old package is already gone, the new package is now authoritative;
      // never roll it back and risk leaving the customer with neither package.
      if (!oldPackageIsIntact) {
        forgetPendingReplacement();
        rememberCart(recoveryCart);
        window.location.reload();
        return null;
      }

      const rollbackUpdates = restoreKitUpdates(cart, recoveryCart, newKitId);

      if (Object.keys(rollbackUpdates).length) {
        const rolledBackCart = rememberCart(await postJson('cart/update.js', {
          updates: rollbackUpdates,
          sections: bundledSections()
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'ronini:edit-rollback', cart: rolledBackCart }
        }));
      }
      forgetPendingReplacement();
      throw replaceError;
    }

    forgetPendingReplacement();

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart: updatedCart }
    }));
    return updatedCart;
  };

  document.addEventListener('click', async (event) => {
    const sizeButton = event.target.closest('ronini-cart-editor [data-size]');
    if (sizeButton) {
      const editor = sizeButton.closest('ronini-cart-editor');
      editor.querySelectorAll('[data-size]').forEach((button) => button.setAttribute('aria-pressed', String(button === sizeButton)));
      return;
    }

    // Cancel: back to what the cart holds, panel closed, focus on the row it opened from.
    const cancelButton = event.target.closest('ronini-cart-editor [data-cancel]');
    if (cancelButton) {
      const editor = cancelButton.closest('ronini-cart-editor');
      if (editor.classList.contains('is-saving')) return;
      const currentSize = String(editor.dataset.currentSize || '').replace(/\D/g, '');
      editor.querySelectorAll('[data-size]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.size === currentSize));
      });
      const ageSelect = editor.querySelector('[data-age]');
      if (ageSelect) {
        ageSelect.value = editor.dataset.currentAge || ageSelect.value;
        ageSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const dueInput = editor.querySelector('[data-due-date]');
      const dueValue = editor.querySelector('[data-date-value]');
      if (dueInput) {
        const due = parseIsoDate(editor.dataset.currentDueDate);
        dueInput.value = due ? editor.dataset.currentDueDate : '';
        if (dueValue) {
          dueValue.textContent = due ? displayDate(due) : (editor.dataset.datePlaceholder || 'MM/DD/YYYY');
          dueValue.classList.toggle('is-placeholder', !due);
        }
        datePickerState.delete(editor);
      }
      closeDatePicker(editor);
      const status = editor.querySelector('[data-status]');
      if (status) status.textContent = '';
      const details = editor.querySelector('details');
      details?.removeAttribute('open');
      details?.querySelector('summary')?.focus();
      return;
    }

    const saveButton = event.target.closest('ronini-cart-editor [data-save]');
    if (!saveButton) return;

    const editor = saveButton.closest('ronini-cart-editor');
    // Read now: a failed save can re-render the cart and detach this editor.
    const editorInDrawer = !!editor.closest('cart-drawer');
    const selected = editor.querySelector('[data-size][aria-pressed="true"]');
    const status = editor.querySelector('[data-status]');
    const age = editor.querySelector('[data-age]').value;
    const dueDateInput = editor.querySelector('[data-due-date]');
    const isSubscription = editor.dataset.subscription === 'true';
    // Only a subscription carries the Expecting hold date; one-time ships now.
    const dueDate = isSubscription && age === 'expecting' ? (dueDateInput?.value || '') : '';
    const oldKitId = editor.dataset.kitId;
    const size = selected.dataset.size;

    if (!selected || !/^(expecting|\d{1,2}mo)$/.test(age)) {
      status.textContent = editor.dataset.updateError || 'Could not update. Please try again.';
      return;
    }
    // New subscriptions are not offered from 11 months (owner 09-29: 10 months can subscribe; was 10 per UX03).
    if (isSubscription && age !== 'expecting' && Number(String(age).replace('mo', '')) >= 11) {
      status.textContent = editor.dataset.subscriptionAgeLimit || 'Subscriptions start before 11 months. Remove this set and add a one-time set instead.';
      return;
    }
    if (isSubscription && age === 'expecting' && !dueDate) {
      status.textContent = editor.dataset.dueDateRequired;
      editor.querySelector('[data-date-toggle]')?.focus();
      return;
    }
    const currentSize = String(editor.dataset.currentSize || '').replace(/\D/g, '');
    const currentAge = editor.dataset.currentAge || '';
    const currentDueDate = editor.dataset.currentDueDate || '';
    const isUnchanged = currentSize === size && currentAge === age && currentDueDate === dueDate;
    if (isUnchanged) {
      editor.querySelector('details')?.removeAttribute('open');
      status.textContent = '';
      return;
    }
    if (userMutationRunning) return;
    userMutationRunning = true;

    saveButton.disabled = true;
    status.textContent = '';
    const editLabel = editor.querySelector('.ronini-cart-editor__edit');
    if (editLabel && !editLabel.dataset.defaultLabel) editLabel.dataset.defaultLabel = editLabel.textContent;
    setEditorSavingState(editor, true, { size, age, dueDate });
    let updateSucceeded = false;

    try {
      // Always resolve the current server key before editing properties. A line-item
      // key changes when another cart script adjusts properties or selling plans;
      // using the optimistic cache here can make Shopify reject an otherwise valid
      // change request. This is still one fewer mutation than the legacy replace flow.
      await waitForGiftPass();
      const cart = await getCart(true);
      const oldItems = cart.items.filter((item) => item.properties?._kit_id === oldKitId);
      const main = oldItems.find((item) => item.properties?._line_role === 'wrapped');
      if (!main) throw new Error('Main item missing');
      const packageQuantity = oldItems.reduce((quantity, item) => (
        item.properties?._line_role === 'wrapped' ? quantity + Number(item.quantity || 0) : quantity
      ), 0);
      if (packageQuantity < 1) throw new Error('Package quantity missing');

      let newKitId = stableKitId(editor, selected, age, dueDate, main, oldItems);
      // The old package is removed by kit id after the add, so the replacement
      // must never share it.
      // Nor may it take over another kit already in the cart (e.g. switching a
      // 5 oz kit to 8 oz next to an 8 oz kit): every kit keeps its own welcome
      // kit, so the edited kit stays a kit of its own (owner 09-30).
      if (newKitId === oldKitId || cart.items.some((item) => item.properties?._kit_id === newKitId)) {
        newKitId = `${newKitId}-e${Date.now().toString(36)}`;
      }

      const mainProps = updatedProperties(main.properties, size, age, dueDate, newKitId, 'wrapped');
      mainProps._kit_parent = 'true';
      mainProps._variant_title = selected.dataset.variantTitle;
      const hasGlassLine = oldItems.some((item) => item.properties?._line_role === 'glass');
      const preorderText = preorderPropertyFor(editor, selected, size, hasGlassLine);
      if (preorderText) mainProps.Preorder = preorderText;
      mainProps['Baby stage'] = ageLabel(age, mainProps._due_date);
      mainProps['Bottle setup'] = `${sizeLabelFor(size)} · ${sizeMeta[size].flow.charAt(0).toUpperCase()}${sizeMeta[size].flow.slice(1)} flow`;
      if (editor.dataset.subscription === 'true') {
        mainProps[editor.dataset.nextShipmentProperty || 'Next shipment'] = forSet(mainProps, nextShipmentLabel(editor, fulfillmentSchedule(age, size, mainProps._due_date)));
        setUpgradeNote(mainProps, age, size, mainProps._due_date);
      }

      const items = [{ id: Number(selected.dataset.variantId), quantity: packageQuantity, properties: mainProps }];
      if (editor.dataset.subscription === 'true' && selected.dataset.planId) {
        items[0].selling_plan = Number(selected.dataset.planId);

        // One welcome kit per subscribed set (Wrapped or Duo — client 2026-10-01);
        // the gift flow follows the new size.
        const ownsWelcomeGifts = true;
        const giftQuantity = packageQuantity * giftsPerSet(main);
        // QA 2026-10-11: the subscription welcome kit has no Venting Tube since 10-11 (nipples only). Re-adding one here
        // put a $10 "Ronini Venting Tube 2-Pack" line in the cart on every kit edit until the welcome-kit sync removed it;
        // the sync (welcomeTargets) alone decides the launch tube for a coded waitlist order.
        const nipple = oldItems.find((item) => item.properties?._line_role === 'welcome_nipple');
        const chosenFlow = chosenBonusFlow(main.properties);
        const giftNippleVariant = (chosenFlow && nippleVariantFor(chosenFlow, editor)) || Number(selected.dataset.giftVariant || 0);
        if (ownsWelcomeGifts && giftNippleVariant) {
          const props = updatedProperties(nipple?.properties || main.properties, size, age, dueDate, newKitId, 'welcome_nipple');
          props._kit_parent = 'false';
          props._kit_price_mode = 'subscription_gift';
          props._bonus_nipple_flow = chosenFlow || sizeMeta[size].bonus;
          props._nipple_flow = props._bonus_nipple_flow;
          props['_' + (editor.dataset.giftCheckoutProperty || 'Box 1 welcome gift').replace(/^_+/, '')] = editor.dataset.giftCheckoutValue || 'Included with your subscription';
          items.push({ id: giftNippleVariant, quantity: giftQuantity, properties: props });
        }
      }

      const glass = oldItems.find((item) => item.properties?._line_role === 'glass');
      const glassVariantId = size === '240'
        ? editor.getAttribute('data-glass-240-variant')
        : editor.getAttribute('data-glass-160-variant');
      if (glass && !glassVariantId) throw new Error('Glass variant for the new size is missing');
      if (glass && glassVariantId) {
        const props = updatedProperties(glass.properties, size, age, dueDate, newKitId, 'glass');
        props._kit_parent = 'false';
        props._bundle_name = 'The Duo';
        props._bundle_component_count = '2';
        items.push({ id: Number(glassVariantId), quantity: packageQuantity, properties: props });
      }

      oldItems.filter((item) => !['wrapped', 'glass', 'welcome_venting', 'welcome_nipple'].includes(item.properties?._line_role)).forEach((item) => {
        const props = updatedProperties(item.properties, size, age, dueDate, newKitId, item.properties?._line_role || 'addon');
        items.push({ id: item.variant_id, quantity: item.quantity, properties: props });
      });

      const updatedCart = await replaceKit(cart, oldKitId, newKitId, items, 'ronini:edit');
      // null: the page is reloading to show a replacement the server finished on its own.
      if (!updatedCart) return;
      updateSucceeded = true;
    } catch (error) {
      // Say what to do, and keep what the customer chose — also when a rollback
      // re-rendered the cart and replaced this editor.
      const sets = Number(editor.closest('.rnc-item, .ronini-cart-kit')?.querySelector('line-item')?.dataset.roniniDisplayQuantity) || 1;
      const sizeChanged = currentSize !== size;
      let message = editor.dataset.updateError
        || 'We couldn’t save this change. Check your connection, then press Update again. Your choices are still here and your cart hasn’t changed.';
      if (error?.status === 422) {
        message = sizeChanged
          ? (sets > 1
            ? `We don’t have ${sets} sets in ${sizeLabelFor(size)} in stock right now. Lower the quantity or keep ${sizeLabelFor(currentSize)}. Your cart hasn’t changed.`
            : `The ${sizeLabelFor(size)} set is out of stock right now. Your cart hasn’t changed.`)
          : 'This set can’t be updated right now because of stock. Your cart hasn’t changed. Email hi@ronini.com and we’ll sort it out.';
      }
      keepEditorForm(editor, editorInDrawer, oldKitId, { size, age, dueDate }, message);
    } finally {
      if (updateSucceeded && document.contains(editor)) {
        editor.dataset.currentSize = size;
        editor.dataset.currentAge = age;
        editor.dataset.currentDueDate = dueDate;
        setEditorSavingState(editor, false);
        saveButton.disabled = false;
      }
      finishUserMutation();
    }
  });

  /* ------------------------------------------------------------------ add-ons
     "Add to this order": one list under all the cart's items (snippets/rn-cart-addons.liquid,
     owner 2026-10-02; one list since 10-26). Every add re-renders the cart in place from the
     section HTML the cart API returns; nothing navigates. */

  // A fresh cart with its sections (rendered for this page), announced to the theme.
  const refreshCartInPlace = async (baseEvent) => {
    const cart = rememberCart(await postJson('cart/update.js', { updates: {}, sections: bundledSections() }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart }
    }));
    return cart;
  };

  // An ordinary paid line: the retail accessory, no kit or gift properties.
  const addPlainVariant = async (variantId) => {
    const response = await fetchWithTimeout(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }, 8000);
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const error = new Error('Cart add failed');
      // Shopify's own sentence ("All 2 … are in your cart.") is safe text for the customer.
      error.customerMessage = typeof body?.description === 'string' ? body.description : '';
      throw error;
    }
    return refreshCartInPlace('ronini:addon');
  };

  // "Make it a Duo" on a subscription set: the same kit the PDP's Duo button builds —
  // the subscribed Wrapped line flagged as a Duo, a Glass line in the same kit and the
  // one welcome kit per set — so the checkout Discount Function prices it as a Duo.
  const makeSubscriptionKitDuo = async (kitId, glassVariantId, duoPlanId) => {
    await waitForGiftPass();
    const cart = await getCart(true);
    const oldItems = cart.items.filter((item) => item.properties?._kit_id === kitId);
    const main = oldItems.find((item) => isSubscriptionParent(item));
    if (!main || !glassVariantId) throw new Error('Set missing');
    if (oldItems.some((item) => item.properties?._line_role === 'glass')) return refreshCartInPlace('ronini:addon');
    const sets = oldItems.reduce((sum, item) => (
      item.properties?._line_role === 'wrapped' && item.properties?._kit_parent === 'true' ? sum + Number(item.quantity || 0) : sum
    ), 0);
    // C04 (review 10-11): the set moves to the size's Duo plan, so checkout's recurring line shows the Duo renewal
    // (two refill packs per set). Without a Duo plan the set keeps its own plan.
    const planId = Number(duoPlanId) || main.selling_plan_allocation?.selling_plan?.id;
    if (sets < 1 || !planId) throw new Error('Set quantity missing');

    const duoProps = { ...main.properties, _duo_flag: 'true', _duo: 'true' };
    if (Number(duoPlanId)) { duoProps._selling_plan_id = String(duoPlanId); delete duoProps._selling_plan_name; }
    let newKitId = canonicalKitId({ ...main, properties: duoProps }, oldItems);
    // Like an edit, the rebuilt kit stays a kit of its own: it never takes over another
    // Duo kit already in the cart, and never shares the id of the kit it replaces.
    if (newKitId === kitId || cart.items.some((item) => item.properties?._kit_id === newKitId)) {
      newKitId = `${newKitId}-e${Date.now().toString(36)}`;
    }
    duoProps._kit_id = newKitId;

    const editor = document.querySelector(`ronini-cart-editor[data-kit-id="${CSS.escape(kitId)}"]`);
    const nextShipmentKey = editor?.dataset.nextShipmentProperty || 'Next shipment';
    const mainProps = { ...duoProps, _line_role: 'wrapped', _kit_parent: 'true' };
    // A Duo set gets two refill 2-packs per delivery; the label is stored per set.
    if (mainProps[nextShipmentKey] && !/\d+ × /.test(mainProps[nextShipmentKey])) {
      mainProps[nextShipmentKey] = forSet(mainProps, mainProps[nextShipmentKey]);
    }
    // The upgrade note switches to the Duo set price (all four bottles).
    setUpgradeNote(mainProps, mainProps._entry_age || mainProps.baby_stage || '0mo',
      String(mainProps._initial_size || mainProps.initial_size || '160').replace(/\D/g, '') || '160', mainProps._due_date || mainProps.due_date);

    const items = [{ id: Number(main.variant_id), quantity: sets, selling_plan: Number(planId), properties: mainProps }];

    // The Glass pair carries the kit's technical properties, not the customer-facing summary.
    const glassProps = { ...duoProps };
    ['Bottle setup', 'Baby stage', nextShipmentKey, UPGRADE_NOTE_KEY].forEach((key) => { delete glassProps[key]; });
    Object.assign(glassProps, { _line_role: 'glass', _kit_parent: 'false', _bundle_name: 'The Duo', _bundle_component_count: '2' });
    items.push({ id: Number(glassVariantId), quantity: sets, properties: glassProps });

    oldItems.forEach((item) => {
      const role = item.properties?._line_role;
      if (role === 'wrapped' || role === 'glass') return;
      // Welcome gifts: one kit per subscribed set, Wrapped or Duo — not doubled.
      const quantity = isWelcomeGift(item) ? sets * giftsPerSet(main) : Number(item.quantity || 0);
      if (items.some((added) => added.properties._line_role === role && Number(added.id) === Number(item.variant_id))) return;
      items.push({ id: Number(item.variant_id), quantity, properties: { ...item.properties, _kit_id: newKitId, _duo_flag: 'true', _duo: 'true' } });
    });

    return replaceKit(cart, kitId, newKitId, items, 'ronini:addon');
  };

  // "Make it a Duo" on a one-time set: swap the Wrapped pair for the fixed Duo bundle
  // line with the same set quantity (the PDP's one-time Duo). Add first, remove after,
  // so a failure never leaves the customer without their bottles.
  const makeOneTimeKitDuo = async (kitId, size) => {
    const cart = await getCart(true);
    const kitItems = cart.items.filter((item) => item.properties?._kit_id === kitId);
    const parents = kitItems.filter((item) => item.properties?._line_role === 'wrapped' && item.properties?._kit_parent === 'true');
    const bundle = duoBundles[size];
    const sets = parents.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (!parents.length || sets < 1 || !bundle?.variantId || bundle.available === false) throw new Error('Duo bundle unavailable');
    if (kitItems.some((item) => item.selling_plan_allocation || item.properties?._plan === 'subscription')) throw new Error('Not a one-time set');
    if (bundle.max !== null && bundle.max !== undefined && sets > Number(bundle.max)) {
      const error = new Error('Duo bundle stock');
      error.customerMessage = `Only ${bundle.max} Duo set${Number(bundle.max) === 1 ? ' is' : 's are'} available right now.`;
      throw error;
    }

    const additions = [{ id: Number(bundle.variantId), quantity: sets, properties: {
      _line_role: 'duo_bundle', _plan: 'onetime', _initial_size: size, _size: `${size}ml`
    } }];
    // A paid venting add-on of the set stays in the cart as an ordinary line of its own.
    kitItems.filter((item) => item.properties?._line_role === 'venting')
      .forEach((line) => additions.push({ id: Number(line.variant_id), quantity: Number(line.quantity || 0) }));
    const bundleQuantity = (source) => source.items.reduce((total, line) => (
      Number(line.variant_id) === Number(bundle.variantId) ? total + Number(line.quantity || 0) : total
    ), 0);
    await postJson('cart/add.js', { items: additions });

    const afterAdd = await cartWithRetries();
    if (bundleQuantity(afterAdd) < bundleQuantity(cart) + sets) throw new Error('Duo bundle was not added');
    const updates = {};
    afterAdd.items.forEach((line) => {
      if (line.properties?._kit_id === kitId) updates[line.key] = 0;
    });
    const updatedCart = rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'ronini:addon', cart: updatedCart }
    }));
    return updatedCart;
  };

  let addonLive = null;
  const announceAddon = (message) => {
    if (!addonLive) {
      addonLive = document.createElement('p');
      addonLive.className = 'sr-only';
      addonLive.setAttribute('role', 'status');
      document.body.appendChild(addonLive);
    }
    addonLive.textContent = '';
    window.setTimeout(() => { addonLive.textContent = message; }, 50);
  };

  const cartRoots = () => [document.querySelector('cart-drawer'), mainCartSection()].filter(Boolean);

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('select[data-rnc-flow]');
    if (!select) return;
    const row = select.closest('.rnc-addon');
    const button = row?.querySelector('[data-rnc-from-flow]');
    if (button) button.dataset.variantId = select.value;
    const label = row?.querySelector('[data-rnc-flow-label]');
    if (label && select.selectedOptions[0]) label.textContent = select.selectedOptions[0].textContent;
    const price = row?.querySelector('[data-rnc-flow-price]');
    const optionPrice = select.selectedOptions[0]?.dataset.price;
    if (price && optionPrice) price.textContent = optionPrice;
    // The row's thumbnail shows the picked flow (snippets/rn-product-thumb, one URL per option).
    const thumb = select.selectedOptions[0]?.dataset.thumb;
    const img = row?.querySelector('.rnc-addon__img img');
    if (img && thumb && img.getAttribute('src') !== thumb) {
      img.removeAttribute('srcset');
      img.src = thumb;
    }
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('button[data-rnc-add]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const group = button.closest('[data-rnc-addons]');
    const root = button.closest('cart-drawer') || mainCartSection();
    // "Make it a Duo" works on one set: that item shows it is busy while it turns into a Duo.
    const kitItem = button.dataset.rncAdd === 'duo' && root && button.dataset.kitId
      ? root.querySelector(`[data-rnc-item="${CSS.escape(button.dataset.kitId)}"]`) : null;
    const errorNode = group?.querySelector('[data-rnc-addons-error]');
    const label = button.querySelector('[aria-hidden="true"]');
    const idleLabel = label?.textContent || '+ Add';
    const siblings = [...(group?.querySelectorAll('button[data-rnc-add]') || [button])];

    if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
    if (userMutationRunning) await waitForCartIdle();
    userMutationRunning = true;
    siblings.forEach((node) => { node.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding…';
    kitItem?.setAttribute('aria-busy', 'true');

    let succeeded = false;
    try {
      let result;
      if (button.dataset.rncAdd === 'duo') {
        result = button.dataset.mode === 'subscription'
          ? await makeSubscriptionKitDuo(button.dataset.kitId, button.dataset.glassVariant, button.dataset.duoPlan)
          : await makeOneTimeKitDuo(button.dataset.kitId, button.dataset.size);
      } else {
        result = await addPlainVariant(button.dataset.variantId);
      }
      succeeded = true;
      announceAddon(`${button.dataset.added || 'Item'} added to your cart.`);
      if (result === null) return;
      // The cart was re-rendered: keep keyboard focus in the add-on list, or on Checkout once nothing is left to add.
      window.setTimeout(() => {
        const freshRoot = cartRoots().find((node) => node.matches(root?.matches('cart-drawer') ? 'cart-drawer' : '[data-ronini-main-cart]'));
        if (!freshRoot) return;
        // Never a text input: on phones focusing the quantity field raised the keyboard and scrolled the drawer (10-07).
        const target = freshRoot.querySelector('[data-rnc-addons] :is(button[data-rnc-add]:not(:disabled), a.rnc-addon__btn)')
          || document.querySelector(root?.matches('cart-drawer') ? 'cart-drawer .rnc-checkout' : '[data-ronini-main-cart] .ronini-checkout-link');
        target?.focus({ preventScroll: true });
      }, 60);
    } catch (error) {
      if (errorNode && document.contains(errorNode)) {
        errorNode.textContent = error?.customerMessage || 'We couldn’t add this. Please try again.';
        errorNode.hidden = false;
      }
      // Whatever the server holds is what the customer should see.
      currentCart = null;
    } finally {
      if (document.contains(button)) {
        siblings.forEach((node) => { node.disabled = false; });
        button.removeAttribute('aria-busy');
        if (label) label.textContent = idleLabel;
      }
      kitItem?.removeAttribute('aria-busy');
      finishUserMutation();
    }
  });

  let giftReconciliationRunning = false;
  let giftReconciliationDirty = false;

  // Welcome gifts: every subscription kit owns its gifts (_kit_id). One kit
  // (one nipple 2-pack + one venting 2-pack) per subscribed set, Wrapped or Duo
  // (client 2026-10-01), so gift quantity = sets. A cart still holding the
  // doubled Duo gifts from 09-30 is corrected down here on load. Pricing is
  // done by the "Ronini bundle pricing" discount function (same count).
  // Launch-week offer (owner 2026-10-11; one per order since 10-13): while snippets/rn-launch-offer.liquid says the
  // window is open, a coded order gets ONE free Venting Tube 2-pack, held by one of its sets (see welcomeTargets). The
  // checkout Discount Function prices it; after the window the owner list shrinks and the stale tube is removed.
  const launchOffer = (cart) => {
    const el = document.getElementById('rn-launch-offer');
    if (!el) return null;
    let cfg;
    try { cfg = JSON.parse(el.textContent || '{}'); } catch (error) { return null; }
    const now = Date.now();
    let preview = false;
    if (cfg.preview === true) {
      try { preview = sessionStorage.getItem('rn-launch-preview') === '1'; } catch (error) { preview = false; }
    }
    if (!(preview || (now >= Date.parse(cfg.start || '') && now < Date.parse(cfg.end || ''))) || !Number(cfg.ventingVariant)) return null;
    // owner 2026-10-11: only with a personal waitlist code on the cart (the Discount Function checks the same prefix)
    if (cfg.codePrefix) {
      const prefix = String(cfg.codePrefix).toUpperCase();
      // 10-12 (#1170): only a code the cart accepts. A mistyped, used or made-up EARLY15- code stays on the cart
      // marked applicable:false (the discount field shows its error but keeps it), and the tube it pulled in was
      // charged $10 at checkout. A real waitlist code reads applicable in an anonymous cart (tested 10-12); the
      // waitlist-segment check happens only at checkout, so a code checkout rejects can still leave a charged tube.
      const codes = (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [])
        .filter((d) => d && d.applicable !== false)
        .map((d) => String(d && d.code || '').toUpperCase());
      if (!codes.some((code) => code.startsWith(prefix))) return null;
    }
    return cfg;
  };
  const isOneTimeParent = (item) => item.properties?._line_role === 'wrapped'
    && item.properties?._kit_parent === 'true'
    && !item.selling_plan_allocation && item.properties?._plan !== 'subscription';
  const isDuoBundleLine = (item) => item.properties?._line_role === 'duo_bundle' && !item.selling_plan_allocation;

  const welcomeTargets = (cart) => {
    const launch = launchOffer(cart);
    const parents = cart.items.filter((item) => (isSubscriptionParent(item) || (launch && isOneTimeParent(item))) && item.properties?._kit_id);
    const kits = new Map();
    parents.forEach((parent) => {
      const kitId = parent.properties._kit_id;
      const entry = kits.get(kitId) || { parent, quantity: 0, launch: !isSubscriptionParent(parent) };
      entry.quantity += Number(parent.quantity || 0);
      kits.set(kitId, entry);
    });
    if (launch) {
      cart.items.filter(isDuoBundleLine).forEach((bundle) => {
        const kitId = `launch-duo-${bundle.variant_id}`;
        const entry = kits.get(kitId) || { parent: { ...bundle, properties: { ...(bundle.properties || {}), _kit_id: kitId } }, quantity: 0, launch: true };
        entry.quantity += Number(bundle.quantity || 0);
        kits.set(kitId, entry);
      });
    }
    const owners = [...kits.values()]
      .filter((entry) => entry.quantity > 0)
      .map((entry) => ({ parent: entry.parent, quantity: entry.quantity, launch: entry.launch }));
    // 10-12 (owner: honor the early access email, "a Venting Tube 2-pack" with the first order, #1182): a coded cart
    // with no Wrapped set or Duo (Glass Bottle, nipples, a paid tube) gets ONE free tube for the order. Off until the
    // checkout prices it (launch.orderTube, snippets/rn-launch-offer): the bundle Function frees only tubes that
    // belong to a Wrapped set or Duo, so an order tube would be charged $10 today (tested 10-12).
    if (launch && launch.orderTube === true && !owners.length && cart.items.some((item) => !isWelcomeGift(item))) {
      owners.push({ parent: { properties: { _kit_id: 'launch-order', _plan: 'onetime' } }, quantity: 1, launch: true });
    }

    const editors = [...document.querySelectorAll('ronini-cart-editor')];
    // Any editor carries the same store-wide gift variant ids; the owner's
    // editor may not be rendered yet right after a PDP add.
    const anyEditor = editors.find((editor) => editor.dataset.ventingVariant) || null;

    const targets = owners.map(({ parent, quantity, launch: isLaunch }) => {
      const kitId = parent.properties._kit_id;
      if (isLaunch) {
        return {
          parent, kitId, quantity,
          size: String(parent.properties?._initial_size || parent.properties?._size || '').replace(/\D/g, ''),
          giftQuantity: quantity,
          gifts: cart.items.filter((item) => item.properties?._kit_id === kitId && isWelcomeGift(item)),
          giftProperty: '_' + (launch.property || 'Launch week gift').replace(/^_+/, ''),
          giftValue: launch.value || 'Free Venting Tube 2-pack',
          priceMode: 'launch_gift',
          variants: { welcome_venting: Number(launch.ventingVariant) || 0 }
        };
      }
      const ownerEditor = editors.find((editor) => editor.dataset.kitId === kitId) || null;
      const configEditor = ownerEditor || anyEditor;
      const size = String(parent.properties?._initial_size || parent.properties?._size || '').replace(/\D/g, '');
      const sizeButton = size
        ? (ownerEditor?.querySelector(`[data-size="${CSS.escape(size)}"][data-gift-variant]`)
          || document.querySelector(`ronini-cart-editor [data-size="${CSS.escape(size)}"][data-gift-variant]`))
        : null;
      const gifts = cart.items.filter((item) => item.properties?._kit_id === kitId && isWelcomeGift(item));
      return {
        parent,
        kitId,
        size,
        quantity,
        giftQuantity: quantity * giftsPerSet(parent),
        gifts,
        giftProperty: '_' + (configEditor?.dataset.giftCheckoutProperty || 'Box 1 welcome gift').replace(/^_+/, ''),
        giftValue: configEditor?.dataset.giftCheckoutValue || 'Included with your subscription',
        roleMode: launch ? { welcome_venting: 'launch_gift' } : null,
        variants: {
          // owner 2026-10-11: no Venting Tube in the subscription welcome kit any more (0 also removes a stale one);
          // a coded waitlist order (EARLY15-) still gets ONE launch tube per subscribed set, priced by the Function
          welcome_venting: launch ? Number(launch.ventingVariant) || 0 : 0,
          // R01: the parent's chosen spare flow wins over the size default; then whatever gift line is already
          // there; the size default only when nothing was chosen.
          welcome_nipple: Number(
            nippleVariantFor(chosenBonusFlow(parent.properties), ownerEditor || anyEditor)
            || (chosenBonusFlow(parent.properties)
              ? gifts.find((item) => item.properties?._line_role === 'welcome_nipple')?.variant_id
              : 0)
            || sizeButton?.dataset.giftVariant
            || gifts.find((item) => item.properties?._line_role === 'welcome_nipple')?.variant_id
            || 0
          )
        }
      };
    });

    // 10-13 (owner): one free launch tube per ORDER, not per set. Keep it on the set that already holds one (so adding
    // or removing another set does not move it around), else the first eligible set; quantity 1. Every other set's
    // launch tube is dropped (variant 0 removes a stale line). Subscription welcome nipples still follow each set.
    const tubeTargets = targets.filter((target) => target.variants.welcome_venting);
    if (tubeTargets.length) {
      const keeper = tubeTargets.find((target) => target.gifts.some(isLaunchTube)) || tubeTargets[0];
      tubeTargets.forEach((target) => {
        if (target === keeper) {
          target.roleQuantity = { ...(target.roleQuantity || {}), welcome_venting: 1 };
        } else {
          target.variants.welcome_venting = 0;
        }
      });
    }
    return targets.filter((target) => Object.values(target.variants).some(Boolean));
  };

  // Subscription sets whose welcome kit is not what it should be (missing gift line or
  // wrong quantity). Empty when the cart is complete.
  const welcomeKitIssues = (cart) => {
    if (!cart?.items) return [];
    return welcomeTargets(cart).filter((target) => ['welcome_venting', 'welcome_nipple'].some((role) => {
      const variant = target.variants[role];
      if (!variant) return false;
      const quantity = target.gifts
        .filter((item) => item.properties?._line_role === role && Number(item.variant_id) === variant)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      return quantity !== giftQuantityFor(target, role);
    }));
  };

  const reconcileWelcomeGiftsPass = async () => {
    // Always start from the server. A cart snapshot carried by an event can be
    // older than a removal that has already been committed (CART02).
    let cart = await getCart(true);
    for (const oldItem of cart.items || []) {
      const item = cart.items.find((line) => line.key === oldItem.key);
      if (!item) continue;
      const properties = { ...(item.properties || {}) };
      let changed = false;
      for (const name of ['Box 1 welcome gift', 'Launch week gift']) {
        if (!Object.prototype.hasOwnProperty.call(properties, name)) continue;
        properties['_' + name] = properties[name];
        delete properties[name];
        changed = true;
      }
      if (changed) {
        cart = rememberCart(await postJson('cart/change.js', {
          id: item.key, quantity: item.quantity, properties
        }));
      }
    }
    let targets = welcomeTargets(cart);
    const missingItems = [];

    targets.forEach((target) => {
      ['welcome_venting', 'welcome_nipple'].forEach((role) => {
        const variant = target.variants[role];
        if (!variant) return;
        const wantMode = target.roleMode && target.roleMode[role];
        if (target.gifts.some((item) => item.properties?._line_role === role && Number(item.variant_id) === variant
          && (!wantMode || item.properties?._kit_price_mode === wantMode))) return;
        const properties = {
          ...target.parent.properties,
          _line_role: role,
          _kit_parent: 'false',
          _kit_price_mode: (target.roleMode && target.roleMode[role]) || target.priceMode || 'subscription_gift',
          ...((target.roleMode && target.roleMode[role] === 'launch_gift')
            ? { ['_' + (launchOffer(cart)?.property || 'Launch week gift').replace(/^_+/, '')]: launchOffer(cart)?.value || 'Free Venting Tube 2-pack' }
            : { [target.giftProperty]: target.giftValue })
        };
        if (role === 'welcome_nipple') {
          properties._nipple_flow = target.parent.properties?._bonus_nipple_flow || (target.size === '240' ? 'sip' : 'drop');
        }
        missingItems.push({ id: variant, quantity: giftQuantityFor(target, role), properties });
      });
    });

    // Add the desired owner lines before removing stale gifts, so a rejected
    // add leaves the prior free lines intact.
    if (missingItems.length) {
      try {
        await postJson('cart/add.js', { items: checkoutAddOrder(missingItems) });
      } finally {
        // Whatever happened, decide removals from the cart as it is NOW. If the
        // subscription was removed while the add was in flight, the gift that
        // just landed has no owner any more and is removed below.
        cart = await getCart(true);
        targets = welcomeTargets(cart);
      }
    }

    const targetByKit = new Map(targets.map((target) => [target.kitId, target]));
    const kept = new Set();
    const updates = {};
    cart.items.filter(isWelcomeGift).forEach((item) => {
      const role = item.properties?._line_role;
      const target = targetByKit.get(item.properties?._kit_id);
      const desiredVariant = (target && target.variants[role]) || 0;
      const keepKey = `${item.properties?._kit_id}|${role}`;
      const wantMode = target && target.roleMode && target.roleMode[role];
      const keep = !!target
        && desiredVariant > 0
        && Number(item.variant_id) === desiredVariant
        && (!wantMode || item.properties?._kit_price_mode === wantMode)
        && !kept.has(keepKey);
      if (keep) {
        kept.add(keepKey);
        if (Number(item.quantity || 0) !== giftQuantityFor(target, role)) updates[item.key] = giftQuantityFor(target, role);
      } else {
        updates[item.key] = 0;
      }
    });

    // Set components follow the set (owner 09-30): exactly one Glass / paid
    // venting line per set, quantity = the set quantity. Repairs a set whose
    // Wrapped was capped by stock (cart/update.js clamps it silently while the
    // Glass and gifts went up) and duplicate component lines.
    const setQuantity = new Map();
    cart.items.forEach((item) => {
      const kitId = item.properties?._kit_id;
      if (!kitId || item.properties?._line_role !== 'wrapped' || item.properties?._kit_parent !== 'true') return;
      setQuantity.set(kitId, (setQuantity.get(kitId) || 0) + Number(item.quantity || 0));
    });
    const keptComponents = new Set();
    cart.items.forEach((item) => {
      const kitId = item.properties?._kit_id;
      const role = item.properties?._line_role;
      if (!['glass', 'venting'].includes(role) || item.properties?._kit_parent !== 'false' || !setQuantity.has(kitId)) return;
      const keepKey = `${kitId}|${role}|${item.variant_id}`;
      const wanted = keptComponents.has(keepKey) ? 0 : setQuantity.get(kitId);
      keptComponents.add(keepKey);
      if (Number(item.quantity || 0) !== wanted) updates[item.key] = wanted;
    });

    let updatedCart = cart;
    if (Object.keys(updates).length) {
      updatedCart = rememberCart(await postJson('cart/update.js', { updates, sections: bundledSections() }));
    } else {
      rememberCart(updatedCart);
    }

    if (missingItems.length || Object.keys(updates).length) {
      await announceCart(updatedCart, 'ronini:reconcile-gifts');
    }
    return updatedCart;
  };

  const reconcileWelcomeGifts = () => {
    giftReconciliationDirty = true;
    if (giftReconciliationRunning) return giftReconciliationPromise;

    giftReconciliationRunning = true;
    giftReconciliationPromise = (async () => {
      let result = null;
      let passes = 0;
      // Re-run while other cart changes arrived during a pass, so the final
      // state is always computed from the latest committed cart.
      while (giftReconciliationDirty && passes < 5) {
        // Let a package edit/quantity change finish first; its own cart:change
        // marks us dirty again, so the pass below sees the committed result.
        if (userMutationRunning) await waitForCartIdle();
        giftReconciliationDirty = false;
        passes += 1;
        giftPassPromise = reconcileWelcomeGiftsPass();
        try {
          result = await giftPassPromise;
          giftRepairFailed = false;
        } catch (error) {
          giftRepairFailed = true;
          throw error;
        } finally {
          giftPassPromise = null;
        }
      }
      return result;
    })().finally(() => {
      giftReconciliationRunning = false;
      giftReconciliationPromise = null;
      giftReconciliationDirty = false;
    });
    return giftReconciliationPromise;
  };

  // 10-12: a discount code applied or removed in the cart (snippets/cart-discount answers with cart:refresh, not
  // cart:change) can open or close the launch-week tube (EARLY15-), so the gifts are reconciled then too. Passes
  // coalesce and only write when something is missing or stale.
  document.documentElement.addEventListener('cart:refresh', (event) => {
    const fromDiscount = event.target instanceof Element && event.target.closest('cart-discount-field, cart-discount-remove-button');
    if (!fromDiscount) return;
    reconcileWelcomeGifts().catch(() => {}).finally(() => { if (mainCartSection()) refreshMainCart(1); });
  });

  document.documentElement.addEventListener('cart:change', (event) => {
    if ([
      'ronini:reconcile-gifts',
      'ronini:migrate-cart',
      'ronini:migrate-cart-recovery',
      'ronini:consolidate-bundles'
    ].includes(event.detail?.baseEvent)) return;
    reconcileWelcomeGifts(event.detail?.cart).catch(() => {});
  });

  // Owner 10-12: a line above the order cap (snippets/rn-qty-cap, 15 per item; the cart steppers carry it as max) is
  // brought down to it the way a shopper's own edit goes: a set through queuePackageQuantity (its parts and gifts
  // follow), any other line through the theme's change on its quantity input. Runs after load and after each render;
  // a cart within the cap does nothing.
  let clampingOverCap = false;
  const clampOverCap = () => {
    if (clampingOverCap || cartWorkIsPending()) return;
    const seen = new Set();
    const over = [...document.querySelectorAll('line-item .quantity-selector__input[max][data-line-key]')].filter((input) => {
      const max = Number(input.max);
      if (!(max > 0) || !(Number(input.value) > max) || seen.has(input.dataset.lineKey)) return false;
      seen.add(input.dataset.lineKey);
      return true;
    });
    if (!over.length) return;
    clampingOverCap = true;
    try {
      over.forEach((input) => {
        const max = Number(input.max);
        const lineItem = input.closest('line-item');
        const kitId = lineItem?.dataset.roniniKitId;
        if (kitId && lineItem.dataset.roniniKitParent === 'true') {
          showOptimisticPackageQuantity(lineItem, max);
          queuePackageQuantity({ kitId, lineKey: input.dataset.lineKey, lineItem, quantity: max, removing: false });
        } else {
          input.value = String(max);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    } finally {
      clampingOverCap = false;
    }
  };
  document.documentElement.addEventListener('cart:change', () => { window.setTimeout(clampOverCap, 400); });

  const initializeCart = () => {
    const migration = recoverPendingReplacement()
      .catch(() => null)
      .then(() => migrateLegacyCart())
      .then(() => migrateLegacyOneTimeDuos().catch(() => null))
      .then(() => reconcileWelcomeGifts())
      .catch(() => reconcileWelcomeGifts().catch(() => null))
      .finally(() => {
        if (legacyCartMigration === migration) legacyCartMigration = null;
        window.setTimeout(clampOverCap, 0);
      });
    legacyCartMigration = migration;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCart, { once: true });
  } else {
    initializeCart();
  }

})();

/* Apply button for the cart discount field (owner 10-12). The field applies a code on its own "change"
   event (blur or Enter); the button and the Enter key go through that same path, once per code, and Enter
   no longer submits the surrounding cart form. Markup: snippets/cart-discount.liquid. */
(() => {
  if (window.__rnDiscountApply) return;
  window.__rnDiscountApply = true;
  const FIELD = 'cart-discount-field';
  const inputOf = (field) => field && field.querySelector('.form-control input:not([type="hidden"])');
  const apply = (field) => {
    const input = inputOf(field);
    if (!input) return;
    const code = input.value.trim();
    if (!code) {
      input.focus();
      return;
    }
    if (field.getAttribute('aria-busy') === 'true' || input.dataset.rnSent === code) return;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  // What the field already sent, so a blur followed by a click on Apply sends the code once.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.type !== 'hidden' && input.closest(FIELD)) {
      input.dataset.rnSent = input.value.trim();
    }
  }, true);
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-rn-discount-apply]') : null;
    if (!button) return;
    event.preventDefault();
    apply(button.closest(FIELD));
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type === 'hidden') return;
    const field = input.closest(FIELD);
    if (!field) return;
    event.preventDefault();
    apply(field);
  });
  // A rejected code can be sent again once it is corrected or retried.
  document.addEventListener('cart:discount:error', () => {
    document.querySelectorAll(`${FIELD} .form-control input`).forEach((input) => {
      delete input.dataset.rnSent;
    });
  });
})();
